import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MagnifyingGlass, CaretRight, CaretDown, Files, UploadSimple,
  Scales, Scroll, FileText, ListBullets, BookOpen, Certificate,
  Shield, Folders, Eye, PencilSimple, X, Check, Warning,
  Coins, Gavel, Brain, Bank, UsersFour, Handshake, Globe, Lock,
  Database, Leaf, Buildings, FunnelSimple, CloudArrowUp, FilePdf,
  CheckCircle, Circle, SignOut, House, Robot, CalendarBlank, Sparkle,
  WarningCircle, Trash, MagnifyingGlassPlus, ArrowSquareOut, XCircle,
  DownloadSimple, DotsThreeVertical, Bell, Megaphone, ClockCounterClockwise,
  FilePlus, Stack, ArrowsClockwise, ShieldCheck, Archive, ArrowCounterClockwise,
  ArrowsOutSimple, ArrowsInSimple
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import PageBreadcrumb from '@/components/PageBreadcrumb';

// Import refactored library components
import {
  REGULATORY_THEMES,
  DOCUMENT_TYPES,
  REGULATORS,
  isDraftDocument,
  getDocTypeConfig,
  getThemeConfig,
  ArchiveBanner,
  PendingUpdatesBanner,
  LibraryHeader,
  StatsPanel,
  DocumentRow,
  EmptyState
} from '@/components/library';

// ============================================
// MAIN COMPONENT
// Unified Library Page - supports both Master (admin) and Tenant modes
// mode="master" -> AURA Master Library (admin view)
// mode="tenant" -> Tenant Regulatory Library (user view)
// ============================================

const RegulatoryLibraryUnified = ({ mode = 'master' }) => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  
  // Mode-specific configuration
  const isMasterMode = mode === 'master';
  const apiBaseUrl = isMasterMode ? `${API}/admin/aura-library` : `${API}/regulatory-library`;
  const pageTitle = isMasterMode ? 'AURA Master Library' : 'My Regulatory Library';
  // Note: PageBreadcrumb already adds "Dashboard" as first item, so we only pass additional items
  const breadcrumbItems = isMasterMode 
    ? [{ label: 'Admin', href: '/admin' }, { label: 'AURA Master Library' }]
    : [{ label: 'My Regulatory Library' }];
  
  // Core state
  const [documents, setDocuments] = useState([]);
  const [allDocuments, setAllDocuments] = useState([]); // Unfiltered for theme counts
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, published: 0, draft: 0, by_theme: {} });
  
  // Selection state
  const [selectedTheme, setSelectedTheme] = useState(null); // null = all themes
  const [selectedDocType, setSelectedDocType] = useState(null); // null = all types
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedDocIds, setSelectedDocIds] = useState(new Set());
  
  // Resizable sidebar state
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('mlv3_sidebar_width');
    return saved ? parseInt(saved, 10) : 224; // Default 224px (w-56)
  });
  const [isResizing, setIsResizing] = useState(false);
  const SIDEBAR_MIN_WIDTH = 180;
  const SIDEBAR_MAX_WIDTH = 400;
  
  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [exploreMode, setExploreMode] = useState('theme'); // 'theme' or 'instrument'
  const [expandedThemes, setExpandedThemes] = useState(new Set(['AML_CFT_PF']));
  const [expandedInstruments, setExpandedInstruments] = useState(new Set(['act']));
  const [showFilters, setShowFilters] = useState(false);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [showAddThemeModal, setShowAddThemeModal] = useState(false);
  const [showEditThemeModal, setShowEditThemeModal] = useState(false);
  const [themeToEdit, setThemeToEdit] = useState(null);
  const [customThemes, setCustomThemes] = useState([]);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [showClearAllModal, setShowClearAllModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  
  // Archive view state
  const [viewMode, setViewMode] = useState('active'); // 'active' or 'archive'
  const [archivedDocuments, setArchivedDocuments] = useState([]);
  const [loadingArchive, setLoadingArchive] = useState(false);
  
  // Modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showVersionHistoryModal, setShowVersionHistoryModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [documentToEdit, setDocumentToEdit] = useState(null);
  const [documentForHistory, setDocumentForHistory] = useState(null);
  
  // Organizations for publishing (master mode only)
  const [organizations, setOrganizations] = useState([]);
  
  // Tenant mode: pending updates from AURA
  const [pendingUpdates, setPendingUpdates] = useState([]);
  const [pendingUpdatesCount, setPendingUpdatesCount] = useState(0);
  const [showPendingUpdatesModal, setShowPendingUpdatesModal] = useState(false);
  const [acceptingUpdate, setAcceptingUpdate] = useState(null);

  // Publish to Regulatory Universe (Tenant mode)
  const [showPublishToUniverseModal, setShowPublishToUniverseModal] = useState(false);
  const [documentToPublish, setDocumentToPublish] = useState(null);
  const [publishingToUniverse, setPublishingToUniverse] = useState(false);

  // Fullscreen/Expanded View
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Sidebar resize handlers
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const newWidth = e.clientX - 256; // Account for main sidebar (256px)
      if (newWidth >= SIDEBAR_MIN_WIDTH && newWidth <= SIDEBAR_MAX_WIDTH) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      if (isResizing) {
        setIsResizing(false);
        localStorage.setItem('mlv3_sidebar_width', sidebarWidth.toString());
      }
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, sidebarWidth]);

  // ============================================
  // DATA FETCHING
  // ============================================

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      // Use different endpoint based on mode
      const endpoint = isMasterMode 
        ? `${API}/admin/aura-library/documents`
        : `${API}/regulatory-library/all-documents`;
      
      const allRes = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Process all documents - determine draft status from title
      const allDocs = (allRes.data.documents || []).map(doc => ({
        ...doc,
        is_draft: isDraftDocument(doc.name || doc.document_name),
        status: isDraftDocument(doc.name || doc.document_name) ? 'draft' : 'published',
        // Normalize theme ID for tenant docs (lowercase to match master format)
        theme: doc.theme ? doc.theme.replace(/_/g, '_').replace(/^([a-z])/, (m) => m.toUpperCase()) : doc.theme
      }));
      
      setAllDocuments(allDocs);
      
      // Filter if theme selected
      if (selectedTheme) {
        setDocuments(allDocs.filter(doc => doc.theme === selectedTheme));
      } else {
        setDocuments(allDocs);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Failed to load documents');
      setDocuments([]);
      setAllDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [token, selectedTheme, isMasterMode]);

  const fetchStats = useCallback(async () => {
    try {
      const endpoint = isMasterMode 
        ? `${API}/admin/aura-library/stats`
        : `${API}/regulatory-library/stats`;
      
      const res = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Use stats directly from backend (already calculates draft count)
      setStats({
        total: res.data.total || 0,
        published: res.data.published || 0,
        draft: res.data.draft || 0,
        by_theme: res.data.by_theme || {}
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [token, isMasterMode]);

  // Fetch organizations (master mode only)
  const fetchOrganizations = useCallback(async () => {
    if (!isMasterMode) return;
    try {
      const res = await axios.get(`${API}/admin/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrganizations(res.data.organizations || []);
    } catch (error) {
      console.log('Organizations not available');
      setOrganizations([]);
    }
  }, [token, isMasterMode]);

  // Fetch pending updates (tenant mode only)
  const fetchPendingUpdates = useCallback(async () => {
    if (isMasterMode) return;
    try {
      const res = await axios.get(`${API}/regulatory-library/pending-updates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingUpdates(res.data.requests || []);
      setPendingUpdatesCount(res.data.pending_count || 0);
    } catch (error) {
      console.log('Pending updates not available');
      setPendingUpdates([]);
      setPendingUpdatesCount(0);
    }
  }, [token, isMasterMode]);

  const fetchCustomThemes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/aura-library/custom-themes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Transform backend format to match REGULATORY_THEMES format
      const themes = (res.data.themes || []).map(t => ({
        id: t.id,
        code: t.code || t.id?.substring(0, 3).toUpperCase() || 'CUS',
        label: t.label,
        icon: Shield, // Default icon
        color: t.color || '#6B7280'
      }));
      setCustomThemes(themes);
    } catch (error) {
      console.log('Custom themes not available');
      setCustomThemes([]);
    }
  }, [token]);

  // Fetch archived documents
  const fetchArchivedDocuments = useCallback(async () => {
    setLoadingArchive(true);
    try {
      const endpoint = isMasterMode 
        ? `${API}/admin/aura-library/archive`
        : `${API}/regulatory-library/archive`;
      
      const res = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArchivedDocuments(res.data.documents || []);
    } catch (error) {
      console.log('Archive not available');
      setArchivedDocuments([]);
    } finally {
      setLoadingArchive(false);
    }
  }, [token, isMasterMode]);

  useEffect(() => {
    fetchDocuments();
    fetchStats();
    fetchOrganizations();
    fetchCustomThemes();
    fetchPendingUpdates();
  }, [fetchDocuments, fetchStats, fetchOrganizations, fetchCustomThemes, fetchPendingUpdates]);

  // Fetch archive when switching to archive view
  useEffect(() => {
    if (viewMode === 'archive') {
      fetchArchivedDocuments();
    }
  }, [viewMode, fetchArchivedDocuments]);

  // ============================================
  // COMPUTED VALUES
  // ============================================

  const filteredDocuments = useMemo(() => {
    let filtered = [...documents];
    
    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(doc => 
        (doc.name || '').toLowerCase().includes(query) ||
        (doc.document_name || '').toLowerCase().includes(query) ||
        (doc.reference_number || '').toLowerCase().includes(query) ||
        (doc.description || '').toLowerCase().includes(query)
      );
    }
    
    // Filter by theme
    if (selectedTheme) {
      filtered = filtered.filter(doc => doc.theme === selectedTheme);
    }
    
    // Filter by document type
    if (selectedDocType) {
      filtered = filtered.filter(doc => 
        (doc.document_type || '').toLowerCase() === selectedDocType
      );
    }
    
    return filtered;
  }, [documents, searchQuery, selectedTheme, selectedDocType]);

  // Count documents by theme and type (use allDocuments for stable counts)
  const themeCounts = useMemo(() => {
    const counts = {};
    REGULATORY_THEMES.forEach(theme => {
      const themeDocs = allDocuments.filter(d => d.theme === theme.id);
      counts[theme.id] = {
        total: themeDocs.length,
        byType: {}
      };
      DOCUMENT_TYPES.forEach(type => {
        counts[theme.id].byType[type.id] = themeDocs.filter(
          d => (d.document_type || '').toLowerCase() === type.id
        ).length;
      });
    });
    return counts;
  }, [allDocuments]);

  // Count documents by instrument (document type) and theme
  const instrumentCounts = useMemo(() => {
    const counts = {};
    DOCUMENT_TYPES.forEach(type => {
      const typeDocs = allDocuments.filter(d => (d.document_type || '').toLowerCase() === type.id);
      counts[type.id] = {
        total: typeDocs.length,
        byTheme: {}
      };
      REGULATORY_THEMES.forEach(theme => {
        counts[type.id].byTheme[theme.id] = typeDocs.filter(d => d.theme === theme.id).length;
      });
    });
    return counts;
  }, [allDocuments]);

  // ============================================
  // HANDLERS
  // ============================================

  const toggleThemeExpand = (themeId) => {
    setExpandedThemes(prev => {
      const next = new Set(prev);
      if (next.has(themeId)) {
        next.delete(themeId);
      } else {
        next.add(themeId);
      }
      return next;
    });
  };

  const toggleInstrumentExpand = (typeId) => {
    setExpandedInstruments(prev => {
      const next = new Set(prev);
      if (next.has(typeId)) {
        next.delete(typeId);
      } else {
        next.add(typeId);
      }
      return next;
    });
  };

  const handleThemeClick = (themeId) => {
    setSelectedTheme(themeId === selectedTheme ? null : themeId);
    setSelectedDocType(null);
  };

  const handleDocTypeClick = (themeId, typeId) => {
    setSelectedTheme(themeId);
    setSelectedDocType(typeId === selectedDocType ? null : typeId);
  };

  // Instrument mode handlers
  const handleInstrumentClick = (typeId) => {
    setSelectedDocType(typeId === selectedDocType ? null : typeId);
    setSelectedTheme(null);
  };

  const handleInstrumentThemeClick = (typeId, themeId) => {
    setSelectedDocType(typeId);
    setSelectedTheme(themeId === selectedTheme ? null : themeId);
  };

  const handleDocumentClick = (doc) => {
    setSelectedDocument(doc);
  };

  const handleReviewClick = (doc, e) => {
    e.stopPropagation();
    setSelectedDocument(doc);
    setShowDetailPanel(true);
  };

  const handleCloseDetailPanel = () => {
    setShowDetailPanel(false);
    setSelectedDocument(null);
  };

  const handleViewPdf = async (doc) => {
    if (!doc.id) {
      toast.error('Document ID not available');
      return;
    }
    
    try {
      // Fetch the PDF with authentication
      const response = await axios.get(
        `${API}/admin/aura-library/documents/${doc.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create a blob URL and open in new tab
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      
      // Clean up the blob URL after a delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      console.error('Error viewing PDF:', error);
      toast.error(error.response?.data?.detail || 'Failed to load PDF');
    }
  };
  
  // Sync a single document to all tenant libraries
  const handleSyncToTenants = async (doc, e) => {
    e?.stopPropagation();
    
    if (doc.is_draft) {
      toast.error('Cannot sync draft documents. Publish the document first.');
      return;
    }
    
    try {
      const res = await axios.post(
        `${API}/admin/aura-library/sync-to-tenants/${doc.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        toast.success(`Document synced to ${res.data.tenant_count || 0} tenant libraries`);
        fetchDocuments();
        fetchStats();
      } else {
        toast.error(res.data.error || 'Failed to sync document');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync document to tenants');
    }
  };

  // Bulk sync all selected documents to tenant libraries
  const [bulkSyncing, setBulkSyncing] = useState(false);
  
  const handleBulkSyncToTenants = async () => {
    if (selectedDocIds.size === 0) {
      toast.error('No documents selected');
      return;
    }
    
    // Filter out drafts from selection
    const selectedDocs = documents.filter(d => selectedDocIds.has(d.id));
    const publishedDocs = selectedDocs.filter(d => !d.is_draft);
    
    if (publishedDocs.length === 0) {
      toast.error('No published documents to sync. Draft documents cannot be synced.');
      return;
    }
    
    setBulkSyncing(true);
    let successCount = 0;
    let failCount = 0;
    let alreadySyncedCount = 0;
    
    try {
      for (const doc of publishedDocs) {
        try {
          const res = await axios.post(
            `${API}/admin/aura-library/sync-to-tenants/${doc.id}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (res.data.success) {
            if (res.data.already_synced) {
              alreadySyncedCount++;
            } else {
              successCount++;
            }
          } else {
            failCount++;
          }
        } catch (error) {
          failCount++;
        }
      }
      
      // Show summary toast
      if (successCount > 0 || alreadySyncedCount > 0) {
        let message = [];
        if (successCount > 0) message.push(`${successCount} new`);
        if (alreadySyncedCount > 0) message.push(`${alreadySyncedCount} re-synced`);
        if (failCount > 0) message.push(`${failCount} failed`);
        toast.success(`Bulk sync complete: ${message.join(', ')}`);
      } else if (failCount > 0) {
        toast.error(`Failed to sync ${failCount} documents`);
      }
      
      // Clear selection and refresh
      setSelectedDocIds(new Set());
      fetchDocuments();
      fetchStats();
    } catch (error) {
      toast.error('Bulk sync failed');
    } finally {
      setBulkSyncing(false);
    }
  };

  // Replace file for an existing document
  const replaceFileInputRef = useRef(null);
  const [replacingFile, setReplacingFile] = useState(false);
  
  const handleReplaceFile = async (doc, file) => {
    if (!file || !doc?.id) return;
    
    setReplacingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(
        `${API}/admin/aura-library/documents/${doc.id}/replace-file`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      if (res.data.success) {
        toast.success(`File replaced: ${res.data.original_filename}`);
        fetchDocuments();
        // Update selected document with new file info
        if (selectedDocument?.id === doc.id) {
          setSelectedDocument({
            ...selectedDocument,
            original_filename: res.data.original_filename,
            file_size_kb: res.data.file_size_kb
          });
        }
      } else {
        toast.error(res.data.error || 'Failed to replace file');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to replace file');
    } finally {
      setReplacingFile(false);
      if (replaceFileInputRef.current) {
        replaceFileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteClick = (doc, e) => {
    e.stopPropagation();
    setDocumentToDelete(doc);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!documentToDelete) return;
    
    try {
      await axios.delete(`${API}/admin/aura-library/documents/${documentToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document deleted successfully');
      setShowDeleteModal(false);
      setDocumentToDelete(null);
      if (selectedDocument?.id === documentToDelete.id) {
        setSelectedDocument(null);
        setShowDetailPanel(false);
      }
      fetchDocuments();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleEditClick = (doc) => {
    setDocumentToEdit(doc);
    setShowEditModal(true);
  };

  // State for impact analysis
  const [analyzingImpact, setAnalyzingImpact] = useState(false);
  const [showImpactModal, setShowImpactModal] = useState(false);
  const [impactResults, setImpactResults] = useState(null);
  const [impactDirective, setImpactDirective] = useState(null);

  // Handle Analyze Policy Impact
  const handleAnalyzeImpact = async (doc) => {
    setAnalyzingImpact(true);
    setImpactDirective(doc);
    setShowImpactModal(true);
    
    try {
      const formData = new FormData();
      formData.append('directive_id', doc.id);
      formData.append('directive_title', doc.name || doc.title || 'Unknown');
      formData.append('directive_theme', doc.theme || '');
      formData.append('directive_summary', doc.description || doc.summary || '');
      
      const response = await axios.post(`${API}/policy-library/analyze-regulatory-impact`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImpactResults(response.data);
      
      if (response.data.alerts_created > 0) {
        toast.success(`${response.data.alerts_created} policy alerts created`);
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impact analysis failed');
      setShowImpactModal(false);
    } finally {
      setAnalyzingImpact(false);
    }
  };

  const toggleDocSelection = (docId) => {
    setSelectedDocIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) {
        next.delete(docId);
      } else {
        next.add(docId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    const publishedDocs = filteredDocuments.filter(d => !d.is_draft);
    if (selectedDocIds.size === publishedDocs.length && publishedDocs.length > 0) {
      setSelectedDocIds(new Set());
    } else {
      setSelectedDocIds(new Set(publishedDocs.map(d => d.id)));
    }
  };

  const handleUpload = async (formData) => {
    try {
      const endpoint = isMasterMode 
        ? `${API}/admin/aura-library/upload`
        : `${API}/regulatory-library/upload`;
      
      await axios.post(endpoint, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Document uploaded successfully');
      setShowUploadModal(false);
      fetchDocuments();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    }
  };

  // Handle accepting pending AURA update (tenant mode)
  const handleAcceptPendingUpdate = async (requestId) => {
    setAcceptingUpdate(requestId);
    try {
      await axios.post(`${API}/regulatory-library/pending-updates/${requestId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document added to your library');
      fetchPendingUpdates();
      fetchDocuments();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept update');
    } finally {
      setAcceptingUpdate(null);
    }
  };

  // Handle declining pending AURA update (tenant mode)
  const handleDeclinePendingUpdate = async (requestId) => {
    setAcceptingUpdate(requestId);
    try {
      await axios.post(`${API}/regulatory-library/pending-updates/${requestId}/decline`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Update declined');
      fetchPendingUpdates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline update');
    } finally {
      setAcceptingUpdate(null);
    }
  };

  // Handle publish to Regulatory Universe (tenant mode)
  const handlePublishToUniverse = async (publishData) => {
    if (!documentToPublish) return;
    
    setPublishingToUniverse(true);
    try {
      await axios.post(
        `${API}/regulatory-library/documents/${documentToPublish.id}/publish-to-universe`,
        publishData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Published to Regulatory Universe');
      setShowPublishToUniverseModal(false);
      setDocumentToPublish(null);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish to universe');
    } finally {
      setPublishingToUniverse(false);
    }
  };

  // Handle unpublish from Regulatory Universe (tenant mode)
  const handleUnpublishFromUniverse = async (doc) => {
    if (!confirm('Are you sure you want to unpublish this from the Regulatory Universe?')) return;
    
    try {
      await axios.post(
        `${API}/regulatory-library/documents/${doc.id}/unpublish-from-universe`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Unpublished from Regulatory Universe');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unpublish');
    }
  };

  // Export documents to CSV
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (selectedTheme) params.append('theme', selectedTheme);
      if (selectedDocType) params.append('document_type', selectedDocType);
      
      const res = await axios.get(`${API}/admin/aura-library/documents/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const docs = res.data.documents || [];
      if (docs.length === 0) {
        toast.error('No documents to export');
        return;
      }
      
      // Convert to CSV
      const headers = Object.keys(docs[0]);
      const csvContent = [
        headers.join(','),
        ...docs.map(row => headers.map(h => `"${(row[h] || '').toString().replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      
      // Download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `master_library_export_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      
      toast.success(`Exported ${docs.length} documents`);
    } catch (error) {
      toast.error('Failed to export documents');
    } finally {
      setExporting(false);
    }
  };

  // Clear all documents
  const handleClearAllDocuments = async () => {
    try {
      const res = await axios.delete(`${API}/admin/aura-library/documents/clear-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(res.data.message || 'All documents cleared');
      setShowClearAllModal(false);
      fetchDocuments();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear documents');
    }
  };

  // Delete custom theme
  const handleDeleteTheme = async (themeId) => {
    try {
      const endpoint = isMasterMode 
        ? `${API}/admin/aura-library/custom-themes/${themeId}`
        : `${API}/regulatory-library/custom-themes/${themeId}`;
      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomThemes(prev => prev.filter(t => t.id !== themeId));
      toast.success('Theme deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete theme');
    }
  };

  // Edit theme handler
  const handleEditTheme = (theme) => {
    setThemeToEdit(theme);
    setShowEditThemeModal(true);
  };

  // ============================================
  // RENDER HELPERS
  // ============================================

  const renderExplorePanel = () => {
    // Combine default themes with custom themes
    const allThemes = [...REGULATORY_THEMES, ...customThemes];
    
    return (
      <div className="relative flex">
        <div 
          style={{ width: sidebarWidth }} 
          className="shrink-0 bg-surface border border-border rounded-lg flex flex-col h-full overflow-hidden"
        >
          {/* Explore Header */}
          <div className="p-4 border-b border-border">
            <h3 className="text-xs font-semibold text-text-tertiary uppercase tracking-wider">Explore</h3>
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => {
                  setExploreMode('theme');
                  setSelectedDocType(null);
                }}
                className={`flex-1 px-3 py-1.5 text-xs rounded-md transition-colors ${
                  exploreMode === 'theme' 
                  ? 'bg-purple-500/10 text-purple-600 border border-purple-500/50' 
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              Theme
            </button>
            <button
              onClick={() => {
                setExploreMode('instrument');
                setSelectedTheme(null);
              }}
              className={`flex-1 px-3 py-1.5 text-xs rounded-md transition-colors ${
                exploreMode === 'instrument' 
                  ? 'bg-purple-500/10 text-purple-600 border border-purple-500/50' 
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              Instrument
            </button>
          </div>
        </div>
        
        {/* Tree Content - Conditional on exploreMode */}
        <div className="flex-1 overflow-y-auto py-2">
          {exploreMode === 'theme' ? (
            // ========== THEME MODE ==========
            <>
              {allThemes.map(theme => {
                const counts = themeCounts[theme.id] || { total: 0, byType: {} };
                const isExpanded = expandedThemes.has(theme.id);
                const isSelected = selectedTheme === theme.id && !selectedDocType;
                const ThemeIcon = theme.icon;
                const isCustomTheme = customThemes.some(t => t.id === theme.id);
                
                return (
                  <div key={theme.id} className="group">
                    {/* Theme Row */}
                    <div
                      className={`flex items-center gap-2 px-4 py-2.5 cursor-pointer transition-colors border-l-3 ${
                        isSelected 
                          ? 'bg-purple-500/10 border-l-purple-500 text-purple-600' 
                          : 'border-l-transparent hover:bg-background-hover text-text-primary'
                      }`}
                    >
                      <button
                        onClick={() => toggleThemeExpand(theme.id)}
                        className="p-0.5 hover:bg-background-hover rounded"
                      >
                        {isExpanded ? (
                          <CaretDown size={14} className="text-text-tertiary" />
                        ) : (
                          <CaretRight size={14} className="text-text-tertiary" />
                        )}
                      </button>
                      <div
                        onClick={() => handleThemeClick(theme.id)}
                        className="flex items-center gap-2 flex-1 min-w-0"
                      >
                        <ThemeIcon size={16} style={{ color: theme.color }} />
                        <span className="text-sm font-medium flex-1 truncate">{theme.label}</span>
                        <span className="text-xs text-text-tertiary bg-background px-2 py-0.5 rounded-full shrink-0">
                          {counts.total}
                        </span>
                      </div>
                      {/* Edit/Delete for custom themes */}
                      {isCustomTheme && (
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEditTheme(theme); }}
                            className="p-1 hover:bg-background rounded text-text-tertiary hover:text-purple-600"
                            title="Edit Theme"
                          >
                            <PencilSimple size={12} />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteTheme(theme.id); }}
                            className="p-1 hover:bg-background rounded text-text-tertiary hover:text-red-500"
                            title="Delete Theme"
                          >
                            <Trash size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {/* Document Types under Theme */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          {DOCUMENT_TYPES.filter(type => counts.byType[type.id] > 0).map(type => {
                            const isTypeSelected = selectedTheme === theme.id && selectedDocType === type.id;
                            return (
                              <div
                                key={type.id}
                                onClick={() => handleDocTypeClick(theme.id, type.id)}
                                className={`flex items-center gap-2 pl-10 pr-4 py-2 cursor-pointer transition-colors ${
                                  isTypeSelected 
                                    ? 'bg-purple-500/10 text-purple-600' 
                                    : 'text-text-secondary hover:bg-background-hover hover:text-text-primary'
                                }`}
                              >
                                <span className="text-sm">{type.label}</span>
                                <span className="text-xs text-text-tertiary ml-auto">
                                  {counts.byType[type.id]}
                                </span>
                              </div>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
              
              {/* Add Theme Button */}
              <div className="px-4 pt-2">
                <button
                  onClick={() => setShowAddThemeModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-dashed border-border rounded-lg text-xs text-text-tertiary hover:border-purple-500 hover:text-purple-600 transition-colors"
                  data-testid="mlv3-add-theme-btn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 256 256">
                    <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z" />
                  </svg>
                  Add Theme
                </button>
              </div>
            </>
          ) : (
            // ========== INSTRUMENT MODE ==========
            <>
              {DOCUMENT_TYPES.map(type => {
                const counts = instrumentCounts[type.id] || { total: 0, byTheme: {} };
                const isExpanded = expandedInstruments.has(type.id);
                const isSelected = selectedDocType === type.id && !selectedTheme;
                const TypeIcon = type.icon;
                
                return (
                  <div key={type.id}>
                    {/* Instrument Row */}
                    <div
                      className={`flex items-center gap-2 px-4 py-2.5 cursor-pointer transition-colors border-l-3 ${
                        isSelected 
                          ? 'bg-purple-500/10 border-l-purple-500 text-purple-600' 
                          : 'border-l-transparent hover:bg-background-hover text-text-primary'
                      }`}
                    >
                      <button
                        onClick={() => toggleInstrumentExpand(type.id)}
                        className="p-0.5 hover:bg-background-hover rounded"
                      >
                        {isExpanded ? (
                          <CaretDown size={14} className="text-text-tertiary" />
                        ) : (
                          <CaretRight size={14} className="text-text-tertiary" />
                        )}
                      </button>
                      <div
                        onClick={() => handleInstrumentClick(type.id)}
                        className="flex items-center gap-2 flex-1"
                      >
                        <TypeIcon size={16} style={{ color: type.color }} />
                        <span className="text-sm font-medium flex-1 truncate">{type.label}</span>
                        <span className="text-xs text-text-tertiary bg-background px-2 py-0.5 rounded-full">
                          {counts.total}
                        </span>
                      </div>
                    </div>
                    
                    {/* Themes under Instrument */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          {allThemes.filter(theme => counts.byTheme[theme.id] > 0).map(theme => {
                            const isThemeSelected = selectedDocType === type.id && selectedTheme === theme.id;
                            const ThemeIcon = theme.icon;
                            return (
                              <div
                                key={theme.id}
                                onClick={() => handleInstrumentThemeClick(type.id, theme.id)}
                                className={`flex items-center gap-2 pl-10 pr-4 py-2 cursor-pointer transition-colors ${
                                  isThemeSelected 
                                    ? 'bg-purple-500/10 text-purple-600' 
                                    : 'text-text-secondary hover:bg-background-hover hover:text-text-primary'
                                }`}
                              >
                                <ThemeIcon size={14} style={{ color: theme.color }} />
                                <span className="text-sm truncate">{theme.label}</span>
                                <span className="text-xs text-text-tertiary ml-auto">
                                  {counts.byTheme[theme.id]}
                                </span>
                              </div>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </>
          )}
        </div>
        
        {/* Stats Section */}
        <div className="p-4 border-t border-border">
          <h4 className="text-xs font-semibold text-text-tertiary uppercase tracking-wider mb-3">Stats</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-text-secondary">Total:</span>
              <span className="text-text-primary font-medium">{stats.total}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">Published:</span>
              <span className="text-emerald-600 font-medium">{stats.published}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">Draft:</span>
              <span className="text-amber-600 font-medium">{stats.draft}</span>
            </div>
          </div>
        </div>
        </div>
        
        {/* Resize Handle */}
        <div
          onMouseDown={handleMouseDown}
          className={`w-2 cursor-col-resize flex items-center justify-center group hover:bg-purple-500/20 transition-colors ${
            isResizing ? 'bg-purple-500/30' : ''
          }`}
          title="Drag to resize sidebar"
        >
          <div className={`w-0.5 h-8 rounded-full bg-border group-hover:bg-purple-500 transition-colors ${
            isResizing ? 'bg-purple-500' : ''
          }`} />
        </div>
      </div>
    );
  };

  const renderDocumentTable = () => {
    const publishedDocs = filteredDocuments.filter(d => !d.is_draft);
    const allPublishedSelected = publishedDocs.length > 0 && 
      publishedDocs.every(d => selectedDocIds.has(d.id));
    
    return (
      <div className="flex-1 min-w-0 flex flex-col bg-surface border border-border rounded-lg overflow-hidden">
        {/* Table Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-background-hover">
          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlass 
                size={16} 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" 
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search documents..."
                className="w-52 pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text-primary placeholder-text-tertiary focus:outline-none focus:border-purple-500"
              />
            </div>
            
            {/* Export CSV */}
            <button
              onClick={handleExportCSV}
              disabled={exporting || filteredDocuments.length === 0}
              className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg text-sm text-text-secondary hover:border-purple-500 disabled:opacity-50"
              title="Export filtered documents to CSV"
              data-testid="mlv3-export-btn"
            >
              <DownloadSimple size={16} />
              {exporting ? 'Exporting...' : 'Export'}
            </button>
            
            {/* Archive Toggle */}
            <button
              onClick={() => setViewMode(viewMode === 'active' ? 'archive' : 'active')}
              className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm transition-colors ${
                viewMode === 'archive' 
                  ? 'border-amber-500 bg-amber-500/10 text-amber-600' 
                  : 'border-border text-text-secondary hover:border-amber-500'
              }`}
              title={viewMode === 'archive' ? 'View active documents' : 'View archived documents'}
              data-testid="archive-toggle-btn"
            >
              {viewMode === 'archive' ? (
                <>
                  <ArrowCounterClockwise size={16} />
                  Back to Active
                </>
              ) : (
                <>
                  <Archive size={16} />
                  Archive
                </>
              )}
            </button>
            
            {/* Upload Button */}
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium transition-colors"
              data-testid="mlv3-upload-btn"
              disabled={viewMode === 'archive'}
              style={{ opacity: viewMode === 'archive' ? 0.5 : 1 }}
            >
              <UploadSimple size={16} />
              Upload
            </button>
            
            {/* Expand/Fullscreen Button */}
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm transition-colors ${
                isFullscreen 
                  ? 'border-purple-500 bg-purple-500/10 text-purple-600' 
                  : 'border-border text-text-secondary hover:border-purple-500'
              }`}
              title={isFullscreen ? 'Exit fullscreen' : 'Expand to fullscreen'}
              data-testid="fullscreen-toggle-btn"
            >
              {isFullscreen ? (
                <>
                  <ArrowsInSimple size={16} />
                  Exit
                </>
              ) : (
                <>
                  <ArrowsOutSimple size={16} />
                  Expand
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* Archive Banner */}
        {viewMode === 'archive' && (
          <div className="mx-4 mb-4 px-4 py-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-center gap-3">
            <Archive size={20} className="text-amber-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-700">Viewing Archived Documents</p>
              <p className="text-xs text-amber-600">These documents have been superseded by newer versions</p>
            </div>
            <span className="px-2 py-1 bg-amber-500/20 text-amber-700 text-xs font-medium rounded">
              {archivedDocuments.length} archived
            </span>
          </div>
        )}
        
        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          <table className="w-full">
            <thead className="sticky top-0 bg-surface z-10">
              <tr className="text-left text-xs text-text-tertiary uppercase tracking-wider">
                {viewMode === 'active' && (
                  <th className="w-10 px-3 py-3">
                    <input
                      type="checkbox"
                      checked={allPublishedSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-border bg-background text-purple-500 focus:ring-purple-500"
                    />
                  </th>
                )}
                <th className="px-3 py-3 w-28">Reference</th>
                <th className="px-3 py-3">Name</th>
                <th className="px-3 py-3 w-24">Status</th>
                {!isMasterMode && viewMode === 'active' && <th className="px-3 py-3 w-20">Source</th>}
                {viewMode === 'archive' && <th className="px-3 py-3 w-32">Superseded By</th>}
                <th className="px-3 py-3 w-24">Regulator</th>
                <th className="px-3 py-3 w-28">Theme</th>
                <th className="px-3 py-3 w-24">Type</th>
                <th className="px-3 py-3 w-28">{viewMode === 'archive' ? 'Archived' : 'Created'}</th>
                {viewMode === 'active' && <th className="px-3 py-3 w-20">Synced</th>}
                {/* Reg Universe column - Tenant mode only */}
                {!isMasterMode && viewMode === 'active' && <th className="px-3 py-3 w-28 text-center">Reg Universe</th>}
                <th className="px-3 py-3 w-24 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(viewMode === 'archive' ? loadingArchive : loading) ? (
                <tr>
                  <td colSpan={viewMode === 'archive' ? 9 : (isMasterMode ? 10 : 11)} className="px-4 py-12 text-center text-text-tertiary">
                    Loading documents...
                  </td>
                </tr>
              ) : (viewMode === 'archive' ? archivedDocuments : filteredDocuments).length === 0 ? (
                <tr>
                  <td colSpan={viewMode === 'archive' ? 9 : (isMasterMode ? 10 : 11)} className="px-4 py-12 text-center text-text-tertiary">
                    {viewMode === 'archive' ? (
                      <div className="flex flex-col items-center gap-2">
                        <Archive size={48} className="text-text-tertiary opacity-50" />
                        <span>No archived documents</span>
                        <span className="text-xs">Documents that are superseded will appear here</span>
                      </div>
                    ) : (
                      'No documents found'
                    )}
                  </td>
                </tr>
              ) : (
                (viewMode === 'archive' ? archivedDocuments : filteredDocuments).map(doc => {
                  const isDraft = doc.is_draft;
                  const isArchived = viewMode === 'archive';
                  const isSelected = selectedDocIds.has(doc.id);
                  const isViewing = selectedDocument?.id === doc.id;
                  const docType = getDocTypeConfig(doc.document_type);
                  const DocTypeIcon = docType.icon;
                  const themeConfig = getThemeConfig(doc.theme);
                  const regulator = REGULATORS.find(r => r.id === doc.regulator);
                  
                  // Format date
                  const displayDate = isArchived 
                    ? (doc.archived_at ? new Date(doc.archived_at).toLocaleDateString('en-ZA', {
                        year: 'numeric', month: '2-digit', day: '2-digit'
                      }).replace(/\//g, '/') : '—')
                    : (doc.created_at ? new Date(doc.created_at).toLocaleDateString('en-ZA', {
                        year: 'numeric', month: '2-digit', day: '2-digit'
                      }).replace(/\//g, '/') : '—');
                  
                  return (
                    <tr
                      key={doc.id}
                      onClick={() => !isArchived && handleDocumentClick(doc)}
                      className={`border-b border-border transition-colors ${
                        isArchived 
                          ? 'bg-amber-500/5 opacity-75' 
                          : isViewing 
                            ? 'bg-purple-500/10 cursor-pointer' 
                            : isSelected 
                              ? 'bg-purple-500/5 cursor-pointer' 
                              : 'hover:bg-background-hover cursor-pointer'
                      } ${isDraft ? 'opacity-70' : ''}`}
                    >
                      {/* Checkbox - only for active view */}
                      {viewMode === 'active' && (
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isDraft}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleDocSelection(doc.id);
                            }}
                            className="w-4 h-4 rounded border-border bg-background text-purple-500 focus:ring-purple-500 disabled:opacity-50"
                          />
                        </td>
                      )}
                      {/* Reference */}
                      <td className="px-3 py-3">
                        <span className="text-xs font-mono text-text-secondary">
                          {doc.reference_number || '—'}
                        </span>
                      </td>
                      {/* Name */}
                      <td className="px-3 py-3">
                        <div className="font-medium text-text-primary text-sm leading-tight">
                          {doc.name || doc.document_name}
                          {/* Supersession badges */}
                          {doc.is_superseded && (
                            <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-500/10 text-red-500 border border-red-500/20">
                              Superseded
                            </span>
                          )}
                          {doc.supersedes && (
                            <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/10 text-amber-600 border border-amber-500/20">
                              <ArrowsClockwise size={10} />
                              Supersedes
                            </span>
                          )}
                        </div>
                        {doc.supersedes && (
                          <div className="text-xs text-amber-600 mt-0.5" title={`Supersedes: ${doc.supersedes.document_name}`}>
                            ← {doc.supersedes.reference_number}
                          </div>
                        )}
                      </td>
                      {/* Status */}
                      <td className="px-3 py-3">
                        {isArchived ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-600">
                            <Archive size={10} />
                            Archived
                          </span>
                        ) : isDraft ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-600">
                            <Circle size={6} weight="fill" />
                            Draft
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/15 text-emerald-600">
                            <CheckCircle size={10} weight="fill" />
                            Published
                          </span>
                        )}
                      </td>
                      {/* Source - Tenant mode only, active view */}
                      {!isMasterMode && viewMode === 'active' && (
                        <td className="px-3 py-3">
                          {doc.source_type === 'aura_master' || doc.synced_from_aura || doc.source === 'master_library' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-500/15 text-purple-600">
                              <ShieldCheck size={10} weight="fill" />
                              AURA
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-500/15 text-blue-600">
                              <UploadSimple size={10} weight="bold" />
                              My Upload
                            </span>
                          )}
                        </td>
                      )}
                      {/* Superseded By - Archive view only */}
                      {viewMode === 'archive' && (
                        <td className="px-3 py-3">
                          {doc.superseded_by ? (
                            <div className="text-xs">
                              <div className="font-medium text-text-primary truncate max-w-[120px]" title={doc.superseded_by.document_name}>
                                {doc.superseded_by.document_name || 'Unknown'}
                              </div>
                              <div className="text-text-tertiary font-mono">
                                {doc.superseded_by.reference_number || '—'}
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-text-tertiary">—</span>
                          )}
                        </td>
                      )}
                      {/* Regulator */}
                      <td className="px-3 py-3">
                        <span className="text-xs text-text-primary font-medium">
                          {regulator?.id || doc.regulator || '—'}
                        </span>
                      </td>
                      {/* Theme */}
                      <td className="px-3 py-3">
                        <span className="text-xs text-text-secondary">
                          {themeConfig?.code || doc.theme || '—'}
                        </span>
                      </td>
                      {/* Type */}
                      <td className="px-3 py-3">
                        <span 
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium"
                          style={{ 
                            backgroundColor: `${docType.color}15`,
                            color: docType.color 
                          }}
                        >
                          <DocTypeIcon size={10} />
                          {docType.singular}
                        </span>
                      </td>
                      {/* Created / Archived Date */}
                      <td className="px-3 py-3">
                        <span className="text-xs text-text-tertiary">
                          {displayDate}
                        </span>
                      </td>
                      {/* Synced - only for active view */}
                      {viewMode === 'active' && (
                        <td className="px-3 py-3">
                          {doc.synced_to_tenants ? (
                            <span className="text-xs text-blue-600 font-medium">
                              {doc.tenant_count || 0}
                            </span>
                          ) : (
                            <span className="text-xs text-text-tertiary">—</span>
                          )}
                        </td>
                      )}
                      {/* Regulatory Universe - Tenant mode only, active view */}
                      {!isMasterMode && viewMode === 'active' && (
                        <td className="px-3 py-3 text-center">
                          {doc.published_to_universe ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUnpublishFromUniverse(doc);
                              }}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25 transition-colors border border-emerald-500/30"
                              title="Click to unpublish from Regulatory Universe"
                            >
                              <CheckCircle size={12} weight="fill" />
                              Published
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDocumentToPublish(doc);
                                setShowPublishToUniverseModal(true);
                              }}
                              disabled={doc.is_draft}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                                doc.is_draft
                                  ? 'bg-gray-500/10 text-gray-400 cursor-not-allowed'
                                  : 'bg-purple-500/10 text-purple-600 hover:bg-purple-500/20 border border-purple-500/30'
                              }`}
                              title={doc.is_draft ? 'Cannot publish drafts' : 'Publish to Regulatory Universe'}
                            >
                              <Globe size={12} />
                              Publish
                            </button>
                          )}
                        </td>
                      )}
                      {/* Actions */}
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-center gap-0.5">
                          {/* View Metadata */}
                          <button
                            onClick={(e) => handleReviewClick(doc, e)}
                            className="p-1 hover:bg-purple-500/10 rounded transition-colors text-text-tertiary hover:text-purple-600"
                            title="View Metadata"
                            data-testid={`doc-review-${doc.id}`}
                          >
                            <Eye size={16} />
                          </button>
                          {/* Actions only for active docs */}
                          {viewMode === 'active' && (
                            <>
                              {/* Sync to Tenants - Master mode only */}
                              {isMasterMode && (
                                <button
                                  onClick={(e) => handleSyncToTenants(doc, e)}
                                  disabled={doc.is_draft}
                                  className={`p-1 rounded transition-colors ${
                                    doc.is_draft 
                                      ? 'text-text-tertiary/40 cursor-not-allowed' 
                                      : doc.synced_to_tenants 
                                        ? 'text-emerald-500 hover:bg-emerald-500/10' 
                                        : 'text-text-tertiary hover:bg-blue-500/10 hover:text-blue-600'
                                  }`}
                                  title={doc.is_draft ? 'Cannot sync drafts' : doc.synced_to_tenants ? 'Synced - Click to re-sync' : 'Sync to Tenant Libraries'}
                                  data-testid={`doc-sync-${doc.id}`}
                                >
                                  <ArrowsClockwise size={16} />
                                </button>
                              )}
                              {/* Delete */}
                              <button
                                onClick={(e) => handleDeleteClick(doc, e)}
                                className="p-1 hover:bg-red-500/10 rounded transition-colors text-text-tertiary hover:text-red-600"
                                title="Delete"
                              >
                                <Trash size={16} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {/* Action Bar */}
        {selectedDocIds.size > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-surface">
            <span className="text-sm text-text-secondary">
              <strong className="text-text-primary">{selectedDocIds.size}</strong> document(s) selected
            </span>
            <div className="flex items-center gap-2">
              {/* Bulk Sync Button */}
              <button
                onClick={handleBulkSyncToTenants}
                disabled={bulkSyncing}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                data-testid="mlv3-bulk-sync-btn"
              >
                <ArrowsClockwise size={16} className={bulkSyncing ? 'animate-spin' : ''} />
                {bulkSyncing ? 'Syncing...' : 'Sync to Tenants'}
              </button>
              {/* Publish to Client Button */}
              <button
                onClick={() => setShowPublishModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium transition-colors"
                data-testid="mlv3-continue-choose-client-btn"
              >
                Continue: Choose Client →
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDetailPanel = () => {
    // Only show panel when explicitly opened via review icon
    if (!showDetailPanel || !selectedDocument) {
      return null;
    }
    
    const doc = selectedDocument;
    const isDraft = doc.is_draft;
    const docType = getDocTypeConfig(doc.document_type);
    const theme = getThemeConfig(doc.theme);
    
    return (
      <div className="w-80 shrink-0 bg-surface border border-border rounded-lg flex flex-col overflow-hidden shadow-lg">
        {/* Header with Close Button */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-background-hover">
          <h3 className="text-xs font-semibold text-text-tertiary uppercase tracking-wider">
            Document Detail
          </h3>
          <button
            onClick={handleCloseDetailPanel}
            className="p-1 hover:bg-background rounded-lg transition-colors text-text-tertiary hover:text-text-primary"
            title="Close"
          >
            <X size={18} />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Title */}
          <h2 className="text-base font-semibold text-text-primary leading-tight mb-4">
            {doc.name || doc.document_name}
          </h2>
          
          {/* Metadata */}
          <div className="space-y-3 mb-6">
            <div className="flex justify-between items-start">
              <span className="text-xs text-text-tertiary">Reference</span>
              <span className="text-xs text-text-primary font-mono text-right">
                {doc.reference_number || 'N/A'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-text-tertiary">Regulator</span>
              <span className="text-xs text-text-primary">{doc.regulator || 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-text-tertiary">Theme</span>
              <span className="text-xs text-text-primary">{theme.label}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-text-tertiary">Type</span>
              <span className="text-xs text-text-primary">{docType.singular}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-text-tertiary">Effective Date</span>
              <span className="text-xs text-text-primary">{doc.effective_date || 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-text-tertiary">Status</span>
              {isDraft ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-600">
                  <Circle size={6} weight="fill" />
                  Draft
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/15 text-emerald-600">
                  <CheckCircle size={10} weight="fill" />
                  Published
                </span>
              )}
            </div>
            
            {/* Attached File Info */}
            <div className="pt-3 mt-3 border-t border-border/50">
              <div className="flex justify-between items-start">
                <span className="text-xs text-text-tertiary">Attached File</span>
                <span className="text-xs text-text-primary text-right max-w-[150px] truncate" title={doc.original_filename}>
                  {doc.original_filename || 'No file'}
                </span>
              </div>
              {doc.file_size_kb && (
                <div className="flex justify-between items-center mt-1">
                  <span className="text-xs text-text-tertiary">File Size</span>
                  <span className="text-xs text-text-secondary">{doc.file_size_kb} KB</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Description */}
          {doc.description && (
            <div className="pt-4 border-t border-border mb-4">
              <h4 className="text-xs font-semibold text-text-tertiary uppercase tracking-wider mb-2">
                Description
              </h4>
              <p className="text-xs text-text-secondary leading-relaxed">
                {doc.description}
              </p>
            </div>
          )}
          
          {/* Publication History */}
          {doc.publication_history && doc.publication_history.length > 0 && (
            <div className="pt-4 border-t border-border">
              <h4 className="text-xs font-semibold text-text-tertiary uppercase tracking-wider mb-3">
                Publication History
              </h4>
              <div className="space-y-2">
                {doc.publication_history.map((pub, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-xs">
                    <div className="w-6 h-6 rounded bg-purple-500/15 flex items-center justify-center text-purple-600">
                      {pub.client?.charAt(0) || 'C'}
                    </div>
                    <div className="flex-1">
                      <div className="text-text-primary">{pub.client}</div>
                      <div className="text-text-tertiary">{pub.version} • {pub.date}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Supersession Info */}
          {(doc.supersedes || doc.is_superseded) && (
            <div className="pt-4 border-t border-border">
              <h4 className="text-xs font-semibold text-text-tertiary uppercase tracking-wider mb-3">
                Supersession
              </h4>
              {doc.supersedes && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg mb-2">
                  <div className="flex items-center gap-2 text-xs text-amber-700">
                    <ArrowsClockwise size={14} />
                    <span className="font-medium">Supersedes:</span>
                  </div>
                  <p className="text-sm text-text-primary mt-1 truncate">{doc.supersedes.document_name}</p>
                  <p className="text-xs text-text-tertiary font-mono">{doc.supersedes.reference_number}</p>
                </div>
              )}
              {doc.is_superseded && doc.superseded_by && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <div className="flex items-center gap-2 text-xs text-red-600">
                    <WarningCircle size={14} />
                    <span className="font-medium">Superseded by:</span>
                  </div>
                  <p className="text-sm text-text-primary mt-1 truncate">{doc.superseded_by.document_name}</p>
                  <p className="text-xs text-text-tertiary mt-1">
                    {doc.superseded_by.superseded_at ? new Date(doc.superseded_by.superseded_at).toLocaleDateString() : ''}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Actions */}
        <div className="p-4 border-t border-border space-y-2">
          <button 
            onClick={() => handleViewPdf(doc)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <FilePdf size={16} />
            View PDF
          </button>
          
          {/* Analyze Policy Impact - for Directives, Regulations, and Circulars */}
          {['directive', 'regulation', 'circular', 'dir', 'reg', 'cir'].includes((doc.document_type || '').toLowerCase()) && (
            <button 
              onClick={() => handleAnalyzeImpact(doc)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gold hover:bg-gold-hover text-surface rounded-lg text-sm font-medium transition-colors"
            >
              <Brain size={16} />
              Analyze Policy Impact
            </button>
          )}
          
          {/* Replace File - Hidden Input + Button */}
          <input
            type="file"
            ref={replaceFileInputRef}
            accept=".pdf,.doc,.docx"
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.[0]) {
                handleReplaceFile(doc, e.target.files[0]);
              }
            }}
          />
          <button 
            onClick={() => replaceFileInputRef.current?.click()}
            disabled={replacingFile}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 disabled:bg-amber-400 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <CloudArrowUp size={16} className={replacingFile ? 'animate-pulse' : ''} />
            {replacingFile ? 'Replacing...' : 'Replace File'}
          </button>
          
          <button 
            onClick={(e) => handleSyncToTenants(doc, e)}
            disabled={isDraft}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              isDraft 
                ? 'bg-background text-text-tertiary cursor-not-allowed border border-border' 
                : doc.synced_to_tenants
                  ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                  : 'bg-blue-600 hover:bg-blue-500 text-white'
            }`}
          >
            <ArrowsClockwise size={16} />
            {isDraft ? 'Cannot Sync Draft' : doc.synced_to_tenants ? 'Re-sync to Tenants' : 'Sync to Tenant Libraries'}
          </button>
          <button 
            onClick={() => handleEditClick(doc)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-border rounded-lg text-sm text-text-secondary hover:border-purple-500 hover:text-purple-600 transition-colors"
          >
            <PencilSimple size={16} />
            Edit Metadata
          </button>
        </div>
      </div>
    );
  };

  // ============================================
  // UPLOAD MODAL
  // ============================================

  const UploadModal = () => {
    const [file, setFile] = useState(null);
    const [name, setName] = useState('');
    const [theme, setTheme] = useState('AML_CFT_PF');
    const [docType, setDocType] = useState('act');
    const [regulator, setRegulator] = useState('FIC');
    const [description, setDescription] = useState('');
    const [effectiveDate, setEffectiveDate] = useState('');
    const [effectiveDateMissing, setEffectiveDateMissing] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [naviAnalyzed, setNaviAnalyzed] = useState(false);
    const [supersedesDocId, setSupersedesDocId] = useState('');
    const [showSupersessionPicker, setShowSupersessionPicker] = useState(false);
    const [supersessionSearch, setSupersessionSearch] = useState('');
    
    // Duplicate detection state
    const [checkingDuplicate, setCheckingDuplicate] = useState(false);
    const [duplicateWarning, setDuplicateWarning] = useState(null);
    const [fileChecksum, setFileChecksum] = useState(null);
    
    // Calculate file checksum for duplicate detection
    const calculateChecksum = async (fileData) => {
      const buffer = await fileData.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    };
    
    // Check for duplicates when file is selected
    const handleFileSelect = async (selectedFile) => {
      setFile(selectedFile);
      setDuplicateWarning(null);
      setFileChecksum(null);
      
      if (!selectedFile) return;
      
      // Auto-populate name from filename if empty
      if (!name) {
        const cleanName = selectedFile.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ');
        setName(cleanName);
      }
      
      // Only check duplicates in tenant mode
      if (isMasterMode) return;
      
      setCheckingDuplicate(true);
      try {
        // Calculate checksum
        const checksum = await calculateChecksum(selectedFile);
        setFileChecksum(checksum);
        
        // Check for duplicates
        const res = await axios.post(
          `${API}/regulatory-library/check-duplicate`,
          {
            filename: selectedFile.name,
            checksum: checksum,
            name: name || selectedFile.name.replace(/\.[^/.]+$/, '')
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (res.data.has_duplicate) {
          setDuplicateWarning({
            type: res.data.duplicate_type,
            message: res.data.message,
            documents: res.data.matching_documents || []
          });
        }
      } catch (error) {
        console.log('Duplicate check not available:', error);
      } finally {
        setCheckingDuplicate(false);
      }
    };
    
    // Filter documents for supersession picker
    const supersessionCandidates = useMemo(() => {
      if (!supersessionSearch.trim()) {
        return allDocuments.slice(0, 10);
      }
      const search = supersessionSearch.toLowerCase();
      return allDocuments.filter(d => 
        (d.name || d.document_name || '').toLowerCase().includes(search) ||
        (d.reference_number || '').toLowerCase().includes(search)
      ).slice(0, 10);
    }, [allDocuments, supersessionSearch]);
    
    const selectedSupersedes = allDocuments.find(d => d.id === supersedesDocId);
    
    // Analyze document with NAVI
    const analyzeWithNavi = async () => {
      if (!file || !name.trim()) {
        toast.error('Please provide a file and document name first');
        return;
      }
      
      setAnalyzing(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_name', name.trim());
        
        // Use mode-specific endpoint
        const analyzeEndpoint = isMasterMode 
          ? `${API}/admin/aura-library/analyze-upload`
          : `${API}/regulatory-library/analyze-upload`;
        
        const res = await axios.post(analyzeEndpoint, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (res.data.success) {
          // Auto-populate document title if NAVI extracted one
          if (res.data.document_title) {
            setName(res.data.document_title);
          }
          
          // Auto-populate effective date
          if (res.data.effective_date) {
            setEffectiveDate(res.data.effective_date);
            setEffectiveDateMissing(false);
          } else {
            setEffectiveDateMissing(true);
          }
          
          // Auto-populate description
          if (res.data.description) {
            setDescription(res.data.description);
          }
          
          setNaviAnalyzed(true);
          
          // Provide feedback based on extraction results
          const method = res.data.analysis_method === 'llm_knowledge' ? 'LLM knowledge base' : 'document text';
          const titleGenerated = res.data.document_title ? 'title, ' : '';
          
          if (res.data.requires_admin_review) {
            if (res.data.text_extracted_chars === 0 && !res.data.effective_date_found) {
              toast.warning('NAVI could not determine the effective date. Please verify and enter manually.');
            } else if (!res.data.effective_date_found) {
              toast.warning(`NAVI extracted ${titleGenerated}description via ${method} - effective date not found.`);
            } else {
              toast.warning('NAVI analysis complete - please review extracted data');
            }
          } else {
            toast.success(`NAVI extracted ${titleGenerated}effective date, and description from ${method}`);
          }
        } else {
          toast.error(res.data.error || 'NAVI analysis failed');
        }
      } catch (error) {
        toast.error('Failed to analyze document with NAVI');
        console.error('NAVI analysis error:', error);
      } finally {
        setAnalyzing(false);
      }
    };
    
    // Generate description only
    const generateDescription = async () => {
      if (!file || !name.trim()) {
        toast.error('Please provide a file and document name first');
        return;
      }
      
      setAnalyzing(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_name', name.trim());
        
        // Use mode-specific endpoint
        const analyzeEndpoint = isMasterMode 
          ? `${API}/admin/aura-library/analyze-upload`
          : `${API}/regulatory-library/analyze-upload`;
        
        const res = await axios.post(analyzeEndpoint, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (res.data.success && res.data.description) {
          setDescription(res.data.description);
          toast.success('Description generated by NAVI');
        } else {
          toast.error('Could not generate description');
        }
      } catch (error) {
        toast.error('Failed to generate description');
      } finally {
        setAnalyzing(false);
      }
    };
    
    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!file || !name.trim()) {
        toast.error('Please provide a file and document name');
        return;
      }
      
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('theme', theme);
      formData.append('document_type', docType);
      formData.append('regulator', regulator);
      formData.append('description', description);
      formData.append('effective_date', effectiveDate);
      
      // Supersession is now supported in both modes
      if (supersedesDocId) {
        formData.append('supersedes_doc_id', supersedesDocId);
      }
      
      // Field names differ between master and tenant endpoints
      if (isMasterMode) {
        formData.append('name', name.trim());
        formData.append('requires_effective_date_review', effectiveDateMissing ? 'true' : 'false');
        formData.append('is_draft', isDraftDocument(name) ? 'true' : 'false');
      } else {
        // Tenant upload uses document_name
        formData.append('document_name', name.trim());
      }
      
      try {
        await handleUpload(formData);
      } finally {
        setUploading(false);
      }
    };
    
    // Auto-detect draft from name
    const detectedDraft = isDraftDocument(name);
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white border border-border rounded-xl w-full max-w-lg overflow-hidden shadow-xl max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
            <div>
              <h3 className="text-lg font-semibold text-text-primary">Add to Library</h3>
              <p className="text-xs text-text-tertiary mt-1">
                {isMasterMode ? 'Add a new document to the AURA Master Library' : 'Add a new document to your Regulatory Library'}
              </p>
            </div>
            <button
              onClick={() => setShowUploadModal(false)}
              className="p-2 hover:bg-background-hover rounded-lg transition-colors"
            >
              <X size={20} className="text-text-tertiary" />
            </button>
          </div>
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
            {/* File Drop */}
            <div
              onClick={() => document.getElementById('file-input').click()}
              className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-purple-500/50 transition-colors"
            >
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FilePdf size={32} className="text-red-500" />
                  <div className="text-left">
                    <div className="text-sm text-text-primary font-medium">{file.name}</div>
                    <div className="text-xs text-text-tertiary">
                      {(file.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <CloudArrowUp size={32} className="mx-auto text-text-tertiary mb-2" />
                  <p className="text-sm text-text-secondary">Drop file here or click to browse</p>
                  <p className="text-xs text-text-tertiary mt-1">PDF, Word, Excel supported</p>
                </>
              )}
              <input
                id="file-input"
                type="file"
                accept=".pdf,.docx,.xlsx,.doc,.xls"
                onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
                className="hidden"
              />
            </div>
            
            {/* Duplicate Warning (Tenant mode only) */}
            {!isMasterMode && checkingDuplicate && (
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <ArrowsClockwise size={16} className="text-blue-600 animate-spin" />
                <span className="text-xs text-blue-600">Checking for duplicates...</span>
              </div>
            )}
            
            {!isMasterMode && duplicateWarning && (
              <div className="px-4 py-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <WarningCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-700">
                      {duplicateWarning.type === 'exact_checksum' ? 'Exact duplicate found!' : 'Similar document found!'}
                    </p>
                    <p className="text-xs text-amber-600 mt-1">{duplicateWarning.message}</p>
                    
                    {duplicateWarning.documents.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {duplicateWarning.documents.slice(0, 3).map((doc, idx) => (
                          <div key={idx} className="flex items-center gap-2 px-2 py-1.5 bg-white/50 rounded text-xs">
                            <Files size={14} className="text-amber-600" />
                            <span className="text-text-primary font-medium truncate">
                              {doc.name || doc.document_name || 'Unknown'}
                            </span>
                            {doc.reference_number && (
                              <span className="text-text-tertiary">({doc.reference_number})</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        type="button"
                        onClick={() => {
                          setFile(null);
                          setDuplicateWarning(null);
                        }}
                        className="px-3 py-1.5 text-xs font-medium text-amber-700 border border-amber-300 rounded hover:bg-amber-100"
                      >
                        Cancel Upload
                      </button>
                      <button
                        type="button"
                        onClick={() => setDuplicateWarning(null)}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-amber-600 rounded hover:bg-amber-700"
                      >
                        Upload Anyway
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Document Name */}
            <div>
              <label className="block text-xs text-text-tertiary mb-1.5">Document Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Financial Intelligence Centre Act 38 of 2001"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary placeholder-text-tertiary focus:outline-none focus:border-purple-500"
                required
              />
              {/* Draft Detection Notice */}
              {detectedDraft && (
                <div className="flex items-center gap-2 mt-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <Warning size={16} className="text-amber-600" />
                  <span className="text-xs text-amber-600">
                    Document will be saved as <strong>Draft</strong> (title contains "DRAFT")
                  </span>
                </div>
              )}
            </div>
            
            {/* Theme & Type Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-text-tertiary mb-1.5">Theme</label>
                <select
                  value={theme}
                  onChange={(e) => setTheme(e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-purple-500"
                >
                  {REGULATORY_THEMES.map(t => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-text-tertiary mb-1.5">Document Type</label>
                <select
                  value={docType}
                  onChange={(e) => setDocType(e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-purple-500"
                >
                  {DOCUMENT_TYPES.map(t => (
                    <option key={t.id} value={t.id}>{t.singular}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Regulator */}
            <div>
              <label className="block text-xs text-text-tertiary mb-1.5">Regulator</label>
              <select
                value={regulator}
                onChange={(e) => setRegulator(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-purple-500"
              >
                {REGULATORS.map(r => (
                  <option key={r.id} value={r.id}>{r.label}</option>
                ))}
              </select>
            </div>
            
            {/* Effective Date */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs text-text-tertiary">Effective Date</label>
                {effectiveDateMissing && naviAnalyzed && (
                  <span className="flex items-center gap-1 text-xs font-semibold text-amber-600 bg-amber-100 px-2 py-0.5 rounded">
                    <WarningCircle size={12} weight="fill" />
                    Human to populate
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <CalendarBlank size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
                  <input
                    type="date"
                    value={effectiveDate}
                    onChange={(e) => {
                      setEffectiveDate(e.target.value);
                      setEffectiveDateMissing(false);
                    }}
                    placeholder={effectiveDateMissing && naviAnalyzed ? "Human to populate" : ""}
                    className={`w-full pl-9 pr-3 py-2 bg-background border rounded-lg text-sm text-text-primary focus:outline-none focus:border-purple-500 ${
                      effectiveDateMissing && naviAnalyzed ? 'border-amber-500 bg-amber-50' : 'border-border'
                    }`}
                  />
                </div>
              </div>
              {effectiveDateMissing && naviAnalyzed && (
                <div className="flex items-start gap-2 mt-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <Warning size={16} className="text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700">
                    <strong>NAVI could not establish an effective date.</strong> Please review the document and enter the effective date manually.
                  </p>
                </div>
              )}
            </div>
            
            {/* Description */}
            <div>
              <label className="block text-xs text-text-tertiary mb-1.5">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this document..."
                rows={2}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary placeholder-text-tertiary focus:outline-none focus:border-purple-500 resize-none"
              />
            </div>
            
            {/* Supersession Link */}
            <div>
              <label className="block text-xs text-text-tertiary mb-1.5">Supersedes Document (Optional)</label>
              <p className="text-xs text-text-secondary mb-2">
                If this document replaces or amends an existing document, link it here
              </p>
              
              {selectedSupersedes ? (
                <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <ArrowsClockwise size={16} className="text-amber-600" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-text-primary truncate">{selectedSupersedes.name || selectedSupersedes.document_name}</p>
                    <p className="text-xs text-text-tertiary font-mono">{selectedSupersedes.reference_number}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSupersedesDocId('')}
                    className="p-1 hover:bg-amber-500/20 rounded text-amber-600"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowSupersessionPicker(!showSupersessionPicker)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-secondary hover:border-purple-500"
                  >
                    <span>Select document to supersede...</span>
                    <CaretDown size={14} />
                  </button>
                  
                  {showSupersessionPicker && (
                    <div className="absolute z-20 w-full mt-1 bg-surface border border-border rounded-lg shadow-xl max-h-64 overflow-hidden">
                      <div className="p-2 border-b border-border">
                        <input
                          type="text"
                          value={supersessionSearch}
                          onChange={(e) => setSupersessionSearch(e.target.value)}
                          placeholder="Search documents..."
                          className="w-full px-3 py-1.5 bg-background border border-border rounded text-sm focus:outline-none focus:border-purple-500"
                          autoFocus
                        />
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {supersessionCandidates.length === 0 ? (
                          <p className="p-3 text-sm text-text-tertiary text-center">No documents found</p>
                        ) : (
                          supersessionCandidates.map(doc => (
                            <button
                              key={doc.id}
                              type="button"
                              onClick={() => {
                                setSupersedesDocId(doc.id);
                                setShowSupersessionPicker(false);
                                setSupersessionSearch('');
                              }}
                              className="w-full flex items-center gap-2 p-2 hover:bg-background-hover text-left"
                            >
                              <FileText size={16} className="text-text-tertiary shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-text-primary truncate">{doc.name || doc.document_name}</p>
                                <p className="text-xs text-text-tertiary font-mono">{doc.reference_number || doc.id?.substring(0, 12)}</p>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* NAVI Auto-Analyze Button - Always visible but disabled until ready */}
            <div className="space-y-2">
              <button
                type="button"
                onClick={analyzeWithNavi}
                disabled={analyzing || !file || !name.trim()}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  file && name.trim() && !naviAnalyzed
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white'
                    : 'bg-background border border-border text-text-tertiary'
                } ${(!file || !name.trim()) ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {analyzing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    NAVI is analyzing...
                  </>
                ) : (
                  <>
                    <Robot size={18} />
                    Analyze with NAVI
                    <span className="text-xs opacity-75">(auto-fill effective date)</span>
                  </>
                )}
              </button>
              
              {!file && !name.trim() && (
                <p className="text-xs text-text-tertiary text-center">
                  Add file and document name to enable NAVI analysis
                </p>
              )}
            </div>
            
            {naviAnalyzed && (
              <div className="flex items-center gap-2 px-3 py-2 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <CheckCircle size={16} className="text-purple-600" weight="fill" />
                <span className="text-xs text-purple-600">
                  NAVI analysis complete
                </span>
              </div>
            )}
            
            {/* Warning if uploading without NAVI */}
            {file && name.trim() && !naviAnalyzed && !analyzing && (
              <div className="flex items-start gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <Warning size={16} className="text-amber-600 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">
                  <strong>Recommended:</strong> Click "Analyze with NAVI" above to auto-extract the effective date and description from your document.
                </p>
              </div>
            )}
            
            {/* Submit */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 border border-border rounded-lg text-sm text-text-secondary hover:border-purple-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={uploading || !file || !name.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
              >
                {uploading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <UploadSimple size={16} />
                    Add to Library
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    );
  };

  // ============================================
  // PUBLISH MODAL
  // ============================================

  const PublishModal = () => {
    const [selectedOrg, setSelectedOrg] = useState('');
    const [publishing, setPublishing] = useState(false);
    const [step, setStep] = useState(1); // 1: Select Client, 2: Review, 3: Complete
    
    const selectedDocs = documents.filter(d => selectedDocIds.has(d.id));
    
    const handlePublish = async () => {
      if (!selectedOrg) {
        toast.error('Please select a client organization');
        return;
      }
      
      setPublishing(true);
      try {
        // Publish each selected document to the tenant
        for (const doc of selectedDocs) {
          await axios.post(
            `${API}/admin/aura-library/sync-to-tenants/${doc.id}`,
            { organization_id: selectedOrg },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
        
        toast.success(`Published ${selectedDocs.length} documents to client`);
        setStep(3);
        fetchDocuments();
        fetchStats();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to publish documents');
      } finally {
        setPublishing(false);
      }
    };
    
    const closeModal = () => {
      setShowPublishModal(false);
      setSelectedDocIds(new Set());
      setStep(1);
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white border border-border rounded-xl w-full max-w-2xl overflow-hidden shadow-xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div>
              <h3 className="text-lg font-semibold text-text-primary">
                {step === 1 && 'Step 2: Choose Client'}
                {step === 2 && 'Step 3: Review & Confirm'}
                {step === 3 && 'Publication Complete'}
              </h3>
              <p className="text-xs text-text-tertiary mt-1">
                {step === 1 && `Publishing ${selectedDocs.length} documents`}
                {step === 2 && 'Review the documents before publishing'}
                {step === 3 && 'Documents have been published to the client'}
              </p>
            </div>
            <button
              onClick={closeModal}
              className="p-2 hover:bg-background-hover rounded-lg transition-colors"
            >
              <X size={20} className="text-text-tertiary" />
            </button>
          </div>
          
          {/* Content */}
          <div className="p-6">
            {step === 1 && (
              <div className="space-y-4">
                {/* Step Indicator */}
                <div className="flex items-center justify-center gap-2 mb-6">
                  <div className="flex items-center gap-2 text-text-tertiary">
                    <div className="w-6 h-6 rounded-full bg-purple-500/15 text-purple-600 flex items-center justify-center text-xs">✓</div>
                    <span className="text-xs">Select Documents</span>
                  </div>
                  <div className="w-8 h-px bg-border" />
                  <div className="flex items-center gap-2 text-purple-600">
                    <div className="w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs">2</div>
                    <span className="text-xs">Choose Client</span>
                  </div>
                  <div className="w-8 h-px bg-border" />
                  <div className="flex items-center gap-2 text-text-tertiary">
                    <div className="w-6 h-6 rounded-full bg-background-hover flex items-center justify-center text-xs">3</div>
                    <span className="text-xs">Review</span>
                  </div>
                  <div className="w-8 h-px bg-border" />
                  <div className="flex items-center gap-2 text-text-tertiary">
                    <div className="w-6 h-6 rounded-full bg-background-hover flex items-center justify-center text-xs">4</div>
                    <span className="text-xs">Complete</span>
                  </div>
                </div>
                
                <label className="block text-sm text-text-secondary mb-2">Select Client Organization</label>
                <select
                  value={selectedOrg}
                  onChange={(e) => setSelectedOrg(e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:border-purple-500"
                  data-testid="mlv3-publish-org-select"
                >
                  <option value="">Choose a client...</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
                
                <div className="bg-background-hover rounded-lg p-4 mt-4">
                  <h4 className="text-sm font-medium text-text-secondary mb-2">Selected Documents ({selectedDocs.length})</h4>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {selectedDocs.slice(0, 5).map(doc => (
                      <div key={doc.id} className="text-xs text-text-tertiary truncate">
                        • {doc.name || doc.document_name}
                      </div>
                    ))}
                    {selectedDocs.length > 5 && (
                      <div className="text-xs text-text-tertiary">
                        ...and {selectedDocs.length - 5} more
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {step === 2 && (
              <div className="space-y-4">
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Warning size={20} className="text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-amber-600">Confirm Publication</h4>
                      <p className="text-xs text-amber-600/80 mt-1">
                        This will make {selectedDocs.length} documents available in the client's library.
                        They will be able to view and use these documents immediately.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-background-hover rounded-lg p-4">
                  <div className="flex justify-between text-sm mb-3">
                    <span className="text-text-tertiary">Client:</span>
                    <span className="text-text-primary font-medium">
                      {organizations.find(o => o.id === selectedOrg)?.name || 'Unknown'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-text-tertiary">Documents:</span>
                    <span className="text-text-primary font-medium">{selectedDocs.length} documents</span>
                  </div>
                </div>
              </div>
            )}
            
            {step === 3 && (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-emerald-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check size={32} className="text-emerald-600" />
                </div>
                <h4 className="text-lg font-semibold text-text-primary mb-2">Publication Successful</h4>
                <p className="text-sm text-text-tertiary">
                  {selectedDocs.length} documents have been published to{' '}
                  {organizations.find(o => o.id === selectedOrg)?.name || 'the client'}.
                </p>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-border">
            {step === 1 && (
              <>
                <button
                  onClick={closeModal}
                  className="px-4 py-2 border border-border rounded-lg text-sm text-text-secondary hover:border-purple-500"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setStep(2)}
                  disabled={!selectedOrg}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                  data-testid="mlv3-publish-continue-btn"
                >
                  Continue: Review →
                </button>
              </>
            )}
            {step === 2 && (
              <>
                <button
                  onClick={() => setStep(1)}
                  className="px-4 py-2 border border-border rounded-lg text-sm text-text-secondary hover:border-purple-500"
                >
                  ← Back
                </button>
                <button
                  onClick={handlePublish}
                  disabled={publishing}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
                  data-testid="mlv3-publish-confirm-btn"
                >
                  {publishing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Publishing...
                    </>
                  ) : (
                    'Confirm & Publish'
                  )}
                </button>
              </>
            )}
            {step === 3 && (
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Done
              </button>
            )}
          </div>
        </motion.div>
      </div>
    );
  };

  // ============================================
  // DELETE CONFIRMATION MODAL
  // ============================================

  const DeleteModal = () => {
    const [deleting, setDeleting] = useState(false);
    
    const handleDelete = async () => {
      setDeleting(true);
      await handleConfirmDelete();
      setDeleting(false);
    };
    
    if (!documentToDelete) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white border border-border rounded-xl w-full max-w-md overflow-hidden shadow-xl"
        >
          <div className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-500/15 flex items-center justify-center">
                <Trash size={24} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-primary">Delete Document</h3>
                <p className="text-sm text-text-tertiary">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="bg-background-hover rounded-lg p-4 mb-6">
              <p className="text-sm text-text-primary font-medium mb-1">
                {documentToDelete.name || documentToDelete.document_name}
              </p>
              <p className="text-xs text-text-tertiary">
                {documentToDelete.reference_number || documentToDelete.id?.substring(0, 12)}
              </p>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDocumentToDelete(null);
                }}
                className="px-4 py-2 border border-border rounded-lg text-sm text-text-secondary hover:border-text-tertiary"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
              >
                {deleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash size={16} />
                    Delete Document
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  };

  // ============================================
  // PUBLISH TO UNIVERSE MODAL (Tenant Mode)
  // ============================================

  const PublishToUniverseModal = () => {
    const [applicability, setApplicability] = useState('applicable');
    const [complianceStatus, setComplianceStatus] = useState('under_review');
    const [ownerName, setOwnerName] = useState('');
    const [ownerDepartment, setOwnerDepartment] = useState('');
    const [notes, setNotes] = useState('');
    const [impact, setImpact] = useState(3);
    const [probability, setProbability] = useState(3);

    const handlePublish = () => {
      handlePublishToUniverse({
        applicability,
        compliance_status: complianceStatus,
        owner_name: ownerName || undefined,
        owner_department: ownerDepartment || undefined,
        notes: notes || undefined,
        impact,
        probability
      });
    };

    const riskScore = impact * probability;
    const riskLevel = riskScore >= 16 ? 'High' : riskScore >= 9 ? 'Medium' : 'Low';
    const riskColor = riskScore >= 16 ? 'text-red-500' : riskScore >= 9 ? 'text-amber-500' : 'text-green-500';

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/70" onClick={() => setShowPublishToUniverseModal(false)} />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-lg bg-white rounded-xl shadow-xl overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-purple-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Globe size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Publish to Regulatory Universe</h3>
                <p className="text-sm text-white/80 truncate max-w-[300px]">
                  {documentToPublish?.name || documentToPublish?.title}
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-5">
            {/* Applicability */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">Applicability *</label>
              <div className="flex gap-2">
                {[
                  { value: 'applicable', label: 'Applicable', color: 'bg-green-500' },
                  { value: 'not_applicable', label: 'Not Applicable', color: 'bg-gray-400' },
                  { value: 'under_review', label: 'Under Review', color: 'bg-amber-500' }
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setApplicability(opt.value)}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      applicability === opt.value
                        ? `${opt.color} text-white shadow-md`
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Compliance Status */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">Compliance Status *</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'compliant', label: 'Compliant', color: 'bg-emerald-500' },
                  { value: 'partially_compliant', label: 'Partially Compliant', color: 'bg-amber-500' },
                  { value: 'non_compliant', label: 'Non-Compliant', color: 'bg-red-500' },
                  { value: 'under_review', label: 'Under Review', color: 'bg-blue-500' }
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setComplianceStatus(opt.value)}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      complianceStatus === opt.value
                        ? `${opt.color} text-white shadow-md`
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Owner */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1">Owner Name</label>
                <input
                  type="text"
                  value={ownerName}
                  onChange={(e) => setOwnerName(e.target.value)}
                  placeholder="John Smith"
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1">Department</label>
                <input
                  type="text"
                  value={ownerDepartment}
                  onChange={(e) => setOwnerDepartment(e.target.value)}
                  placeholder="Compliance"
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:border-purple-500"
                />
              </div>
            </div>

            {/* Risk Assessment */}
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-900">Risk Assessment</label>
                <span className={`text-sm font-bold ${riskColor}`}>{riskLevel} ({riskScore})</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Impact (1-5)</label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={impact}
                    onChange={(e) => setImpact(parseInt(e.target.value))}
                    className="w-full accent-purple-600"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Low</span>
                    <span className="font-medium">{impact}</span>
                    <span>High</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Probability (1-5)</label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={probability}
                    onChange={(e) => setProbability(parseInt(e.target.value))}
                    className="w-full accent-purple-600"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Low</span>
                    <span className="font-medium">{probability}</span>
                    <span>High</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any relevant notes about this regulation..."
                rows={2}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:border-purple-500 resize-none"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
            <p className="text-xs text-gray-500">
              This will add the regulation to your Regulatory Universe for compliance tracking.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowPublishToUniverseModal(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePublish}
                disabled={publishingToUniverse}
                className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {publishingToUniverse ? (
                  <>
                    <ArrowsClockwise size={14} className="animate-spin" />
                    Publishing...
                  </>
                ) : (
                  <>
                    <Globe size={14} />
                    Publish to Universe
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  };

  // ============================================
  // EDIT METADATA MODAL
  // ============================================

  const EditModal = () => {
    const [editName, setEditName] = useState(documentToEdit?.name || '');
    const [editTheme, setEditTheme] = useState(documentToEdit?.theme || 'AML_CFT_PF');
    const [editDocType, setEditDocType] = useState(documentToEdit?.document_type || 'act');
    const [editRegulator, setEditRegulator] = useState(documentToEdit?.regulator || 'GEN');
    const [editDescription, setEditDescription] = useState(documentToEdit?.description || '');
    const [editEffectiveDate, setEditEffectiveDate] = useState(documentToEdit?.effective_date || '');
    const [saving, setSaving] = useState(false);
    
    useEffect(() => {
      if (documentToEdit) {
        setEditName(documentToEdit.name || '');
        setEditTheme(documentToEdit.theme || 'AML_CFT_PF');
        setEditDocType(documentToEdit.document_type || 'act');
        setEditRegulator(documentToEdit.regulator || 'GEN');
        setEditDescription(documentToEdit.description || '');
        setEditEffectiveDate(documentToEdit.effective_date || '');
      }
    }, [documentToEdit]);
    
    const handleSave = async () => {
      if (!documentToEdit) return;
      
      setSaving(true);
      try {
        const formData = new FormData();
        formData.append('name', editName);
        formData.append('theme', editTheme);
        formData.append('document_type', editDocType);
        formData.append('regulator', editRegulator);
        formData.append('description', editDescription);
        formData.append('effective_date', editEffectiveDate);
        
        await axios.put(
          `${API}/admin/aura-library/documents/${documentToEdit.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast.success('Document updated successfully');
        setShowEditModal(false);
        setDocumentToEdit(null);
        fetchDocuments();
        fetchStats();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update document');
      } finally {
        setSaving(false);
      }
    };
    
    if (!documentToEdit) return null;
    
    const detectedDraft = isDraftDocument(editName);
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white border border-border rounded-xl w-full max-w-lg overflow-hidden shadow-xl max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
            <div>
              <h3 className="text-lg font-semibold text-text-primary">Edit Document</h3>
              <p className="text-xs text-text-tertiary mt-1">Update document metadata</p>
            </div>
            <button
              onClick={() => {
                setShowEditModal(false);
                setDocumentToEdit(null);
              }}
              className="p-2 hover:bg-background-hover rounded-lg transition-colors"
            >
              <X size={20} className="text-text-tertiary" />
            </button>
          </div>
          
          {/* Form */}
          <div className="p-6 space-y-4 overflow-y-auto flex-1">
            {/* Document Name */}
            <div>
              <label className="block text-xs text-text-tertiary mb-1.5">Document Name</label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-purple-500"
              />
              {detectedDraft && (
                <div className="flex items-center gap-2 mt-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <Warning size={16} className="text-amber-600" />
                  <span className="text-xs text-amber-600">
                    Document will be marked as <strong>Draft</strong> (title contains "DRAFT")
                  </span>
                </div>
              )}
            </div>
            
            {/* Theme & Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-text-tertiary mb-1.5">Theme</label>
                <select
                  value={editTheme}
                  onChange={(e) => setEditTheme(e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-purple-500"
                >
                  {REGULATORY_THEMES.map(t => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-text-tertiary mb-1.5">Document Type</label>
                <select
                  value={editDocType}
                  onChange={(e) => setEditDocType(e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-purple-500"
                >
                  {DOCUMENT_TYPES.map(t => (
                    <option key={t.id} value={t.id}>{t.singular}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Regulator */}
            <div>
              <label className="block text-xs text-text-tertiary mb-1.5">Regulator</label>
              <select
                value={editRegulator}
                onChange={(e) => setEditRegulator(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-purple-500"
              >
                {REGULATORS.map(r => (
                  <option key={r.id} value={r.id}>{r.label}</option>
                ))}
              </select>
            </div>
            
            {/* Effective Date */}
            <div>
              <label className="block text-xs text-text-tertiary mb-1.5">Effective Date</label>
              <input
                type="date"
                value={editEffectiveDate}
                onChange={(e) => setEditEffectiveDate(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-purple-500"
              />
            </div>
            
            {/* Description */}
            <div>
              <label className="block text-xs text-text-tertiary mb-1.5">Description</label>
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-purple-500 resize-none"
              />
            </div>
          </div>
          
          {/* Footer */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
            <button
              onClick={() => {
                setShowEditModal(false);
                setDocumentToEdit(null);
              }}
              className="px-4 py-2 border border-border rounded-lg text-sm text-text-secondary hover:border-purple-500"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !editName.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check size={16} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </motion.div>
      </div>
    );
  };

  // ============================================
  // ADD THEME MODAL
  // ============================================

  const AddThemeModal = () => {
    const [themeName, setThemeName] = useState('');
    const [themeCode, setThemeCode] = useState('');
    const [themeColor, setThemeColor] = useState('#A855F7');
    const [saving, setSaving] = useState(false);
    
    // Color options for themes
    const colorOptions = [
      '#F59E0B', '#3B82F6', '#EC4899', '#14B8A6', '#06B6D4',
      '#D4A843', '#0891B2', '#A855F7', '#6366F1', '#10B981',
      '#EF4444', '#8B5CF6', '#F97316', '#84CC16', '#E11D48'
    ];
    
    // Auto-generate code from name
    useEffect(() => {
      if (themeName.trim()) {
        const words = themeName.trim().split(/\s+/);
        const code = words.map(w => w.charAt(0).toUpperCase()).join('').substring(0, 3);
        setThemeCode(code);
      }
    }, [themeName]);
    
    const handleSubmit = async (e) => {
      e.preventDefault();
      
      if (!themeName.trim() || !themeCode.trim()) {
        toast.error('Please provide theme name and code');
        return;
      }
      
      // Check for duplicate code
      const allThemes = [...REGULATORY_THEMES, ...customThemes];
      if (allThemes.some(t => t.code === themeCode.toUpperCase())) {
        toast.error('Theme code already exists');
        return;
      }
      
      setSaving(true);
      try {
        // Use appropriate endpoint based on mode
        const endpoint = isMasterMode 
          ? `${API}/admin/aura-library/custom-themes`
          : `${API}/regulatory-library/custom-themes`;
        
        await axios.post(
          endpoint,
          {
            id: themeName.trim().toLowerCase().replace(/\s+/g, '_'),
            label: themeName.trim(),
            code: themeCode.toUpperCase().substring(0, 3),
            color: themeColor
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Add to local state
        const newTheme = {
          id: themeName.trim().toLowerCase().replace(/\s+/g, '_'),
          code: themeCode.toUpperCase().substring(0, 3),
          label: themeName.trim(),
          icon: Shield, // Default icon
          color: themeColor
        };
        
        setCustomThemes(prev => [...prev, newTheme]);
        toast.success(`Theme "${themeName}" created successfully`);
        setShowAddThemeModal(false);
        setThemeName('');
        setThemeCode('');
        setThemeColor('#A855F7');
      } catch (error) {
        // If backend doesn't support it yet, still add locally
        const newTheme = {
          id: themeName.trim().toLowerCase().replace(/\s+/g, '_'),
          code: themeCode.toUpperCase().substring(0, 3),
          label: themeName.trim(),
          icon: Shield, // Default icon
          color: themeColor
        };
        
        setCustomThemes(prev => [...prev, newTheme]);
        toast.success(`Theme "${themeName}" created (local only)`);
        setShowAddThemeModal(false);
        setThemeName('');
        setThemeCode('');
        setThemeColor('#A855F7');
      } finally {
        setSaving(false);
      }
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white border border-border rounded-xl w-full max-w-md overflow-hidden shadow-xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div>
              <h3 className="text-lg font-semibold text-text-primary">Add New Theme</h3>
              <p className="text-xs text-text-tertiary mt-1">Create a custom regulatory theme</p>
            </div>
            <button
              onClick={() => setShowAddThemeModal(false)}
              className="p-2 hover:bg-background-hover rounded-lg transition-colors"
            >
              <X size={20} className="text-text-tertiary" />
            </button>
          </div>
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Theme Name */}
            <div>
              <label className="block text-xs text-text-tertiary mb-1.5">Theme Name *</label>
              <input
                type="text"
                value={themeName}
                onChange={(e) => setThemeName(e.target.value)}
                placeholder="e.g., Environmental Compliance"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary placeholder-text-tertiary focus:outline-none focus:border-purple-500"
                required
              />
            </div>
            
            {/* Theme Code */}
            <div>
              <label className="block text-xs text-text-tertiary mb-1.5">Theme Code (3 letters) *</label>
              <input
                type="text"
                value={themeCode}
                onChange={(e) => setThemeCode(e.target.value.toUpperCase().substring(0, 3))}
                placeholder="e.g., ENV"
                maxLength={3}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary font-mono uppercase placeholder-text-tertiary focus:outline-none focus:border-purple-500"
                required
              />
              <p className="text-xs text-text-tertiary mt-1">Used in document reference taxonomy (e.g., FIC<strong>{themeCode || 'XXX'}</strong>001ACTP)</p>
            </div>
            
            {/* Theme Color */}
            <div>
              <label className="block text-xs text-text-tertiary mb-1.5">Theme Color</label>
              <div className="flex flex-wrap gap-2">
                {colorOptions.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setThemeColor(color)}
                    className={`w-8 h-8 rounded-lg transition-all ${
                      themeColor === color ? 'ring-2 ring-offset-2 ring-purple-500 scale-110' : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
            
            {/* Preview */}
            <div className="bg-background-hover rounded-lg p-4">
              <h4 className="text-xs text-text-tertiary mb-2">Preview</h4>
              <div className="flex items-center gap-2">
                <Shield size={16} style={{ color: themeColor }} />
                <span className="text-sm font-medium text-text-primary">{themeName || 'Theme Name'}</span>
                <span className="text-xs text-text-tertiary font-mono ml-auto">{themeCode || 'XXX'}</span>
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowAddThemeModal(false)}
                className="px-4 py-2 border border-border rounded-lg text-sm text-text-secondary hover:border-purple-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || !themeName.trim() || !themeCode.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Check size={16} />
                    Create Theme
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    );
  };

  // ============================================
  // EDIT THEME MODAL
  // ============================================

  const EditThemeModal = () => {
    const [themeName, setThemeName] = useState(themeToEdit?.label || '');
    const [themeCode, setThemeCode] = useState(themeToEdit?.code || '');
    const [themeColor, setThemeColor] = useState(themeToEdit?.color || '#A855F7');
    const [saving, setSaving] = useState(false);
    
    const colorOptions = [
      '#F59E0B', '#3B82F6', '#EC4899', '#14B8A6', '#06B6D4',
      '#D4A843', '#0891B2', '#A855F7', '#6366F1', '#10B981',
      '#EF4444', '#8B5CF6', '#F97316', '#84CC16', '#E11D48'
    ];
    
    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!themeName.trim()) return;
      
      setSaving(true);
      try {
        const endpoint = isMasterMode 
          ? `${API}/admin/aura-library/custom-themes/${themeToEdit.id}`
          : `${API}/regulatory-library/custom-themes/${themeToEdit.id}`;
        await axios.put(
          endpoint,
          { label: themeName.trim(), code: themeCode.toUpperCase().substring(0, 3), color: themeColor },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setCustomThemes(prev => prev.map(t => 
          t.id === themeToEdit.id 
            ? { ...t, label: themeName.trim(), code: themeCode.toUpperCase().substring(0, 3), color: themeColor }
            : t
        ));
        toast.success('Theme updated');
        setShowEditThemeModal(false);
        setThemeToEdit(null);
      } catch (error) {
        toast.error('Failed to update theme');
      } finally {
        setSaving(false);
      }
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white border border-border rounded-xl w-full max-w-md overflow-hidden shadow-xl"
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <h3 className="text-lg font-semibold text-text-primary">Edit Theme</h3>
            <button onClick={() => { setShowEditThemeModal(false); setThemeToEdit(null); }} className="p-2 hover:bg-background-hover rounded-lg">
              <X size={20} className="text-text-tertiary" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-xs text-text-tertiary mb-1.5">Theme Name</label>
              <input type="text" value={themeName} onChange={(e) => setThemeName(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm" required />
            </div>
            <div>
              <label className="block text-xs text-text-tertiary mb-1.5">Theme Code</label>
              <input type="text" value={themeCode} onChange={(e) => setThemeCode(e.target.value.toUpperCase().substring(0, 3))}
                maxLength={3} className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm font-mono" />
            </div>
            <div>
              <label className="block text-xs text-text-tertiary mb-1.5">Color</label>
              <div className="flex flex-wrap gap-2">
                {colorOptions.map(color => (
                  <button key={color} type="button" onClick={() => setThemeColor(color)}
                    className={`w-8 h-8 rounded-lg ${themeColor === color ? 'ring-2 ring-offset-2 ring-purple-500' : ''}`}
                    style={{ backgroundColor: color }} />
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => { setShowEditThemeModal(false); setThemeToEdit(null); }}
                className="px-4 py-2 border border-border rounded-lg text-sm text-text-secondary">Cancel</button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    );
  };

  // ============================================
  // CLEAR ALL MODAL
  // ============================================

  const ClearAllModal = () => {
    const [confirmText, setConfirmText] = useState('');
    const [clearing, setClearing] = useState(false);
    
    const handleClear = async () => {
      if (confirmText !== 'DELETE ALL') return;
      setClearing(true);
      await handleClearAllDocuments();
      setClearing(false);
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white border border-border rounded-xl w-full max-w-md overflow-hidden shadow-xl"
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-red-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <WarningCircle size={24} className="text-red-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-red-600">Clear All Documents</h3>
                <p className="text-xs text-red-500/80">This action cannot be undone</p>
              </div>
            </div>
            <button onClick={() => setShowClearAllModal(false)} className="p-2 hover:bg-red-100 rounded-lg">
              <X size={20} className="text-red-400" />
            </button>
          </div>
          <div className="p-6 space-y-4">
            <p className="text-sm text-text-secondary">
              This will permanently delete <strong className="text-red-600">{stats.total} documents</strong> from the 
              Master Library. Tenant assignments will also be removed.
            </p>
            <div>
              <label className="block text-xs text-text-tertiary mb-1.5">
                Type <strong>DELETE ALL</strong> to confirm
              </label>
              <input type="text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE ALL" className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setShowClearAllModal(false)}
                className="px-4 py-2 border border-border rounded-lg text-sm text-text-secondary">Cancel</button>
              <button onClick={handleClear} disabled={clearing || confirmText !== 'DELETE ALL'}
                className="px-4 py-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
                {clearing ? 'Clearing...' : 'Clear All Documents'}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  };

  // ============================================
  // NOTIFY TENANTS MODAL
  // ============================================

  const NotifyModal = () => {
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [priority, setPriority] = useState('normal');
    const [selectedOrgs, setSelectedOrgs] = useState([]);
    const [sending, setSending] = useState(false);
    
    const handleSend = async (e) => {
      e.preventDefault();
      if (!title.trim() || !message.trim()) {
        toast.error('Please provide title and message');
        return;
      }
      
      setSending(true);
      try {
        await axios.post(
          `${API}/admin/notifications/regulatory-update`,
          {
            title: title.trim(),
            message: message.trim(),
            priority,
            organization_ids: selectedOrgs,
            document_ids: Array.from(selectedDocIds)
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast.success('Notification sent to tenants');
        setShowNotifyModal(false);
        setTitle('');
        setMessage('');
        setPriority('normal');
        setSelectedOrgs([]);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to send notification');
      } finally {
        setSending(false);
      }
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white border border-border rounded-xl w-full max-w-lg overflow-hidden shadow-xl"
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Megaphone size={20} className="text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-primary">Notify Tenants</h3>
                <p className="text-xs text-text-tertiary">Send a regulatory update notification</p>
              </div>
            </div>
            <button onClick={() => setShowNotifyModal(false)} className="p-2 hover:bg-background-hover rounded-lg">
              <X size={20} className="text-text-tertiary" />
            </button>
          </div>
          
          <form onSubmit={handleSend} className="p-6 space-y-4">
            {/* Title */}
            <div>
              <label className="block text-xs text-text-tertiary mb-1.5">Notification Title *</label>
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., New FICA Amendment Published" 
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm" required />
            </div>
            
            {/* Message */}
            <div>
              <label className="block text-xs text-text-tertiary mb-1.5">Message *</label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
                placeholder="Describe the regulatory update and any actions required..."
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm resize-none" required />
            </div>
            
            {/* Priority */}
            <div>
              <label className="block text-xs text-text-tertiary mb-1.5">Priority</label>
              <div className="flex gap-2">
                {['normal', 'high', 'urgent'].map(p => (
                  <button key={p} type="button" onClick={() => setPriority(p)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      priority === p 
                        ? p === 'urgent' ? 'bg-red-500 text-white' : p === 'high' ? 'bg-amber-500 text-white' : 'bg-purple-500 text-white'
                        : 'bg-background border border-border text-text-secondary hover:border-purple-500'
                    }`}>
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Target Organizations */}
            <div>
              <label className="block text-xs text-text-tertiary mb-1.5">
                Target Organizations ({selectedOrgs.length === 0 ? 'All' : selectedOrgs.length + ' selected'})
              </label>
              <div className="max-h-32 overflow-y-auto border border-border rounded-lg p-2 space-y-1">
                {organizations.length === 0 ? (
                  <p className="text-xs text-text-tertiary p-2">No organizations found. Notification will be sent to all.</p>
                ) : (
                  organizations.map(org => (
                    <label key={org.id} className="flex items-center gap-2 p-1.5 hover:bg-background rounded cursor-pointer">
                      <input type="checkbox" checked={selectedOrgs.includes(org.id)}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedOrgs(prev => [...prev, org.id]);
                          else setSelectedOrgs(prev => prev.filter(id => id !== org.id));
                        }}
                        className="w-4 h-4 rounded border-border" />
                      <span className="text-sm text-text-primary">{org.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
            
            {/* Selected Documents Info */}
            {selectedDocIds.size > 0 && (
              <div className="bg-purple-50 p-3 rounded-lg">
                <p className="text-xs text-purple-600">
                  <strong>{selectedDocIds.size}</strong> document(s) will be linked to this notification
                </p>
              </div>
            )}
            
            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowNotifyModal(false)}
                className="px-4 py-2 border border-border rounded-lg text-sm text-text-secondary">Cancel</button>
              <button type="submit" disabled={sending || !title.trim() || !message.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
                {sending ? 'Sending...' : 'Send Notification'}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    );
  };

  // ============================================
  // BULK UPLOAD MODAL
  // ============================================

  const BulkUploadModal = () => {
    const [files, setFiles] = useState([]);
    const [theme, setTheme] = useState('AML_CFT_PF');
    const [docType, setDocType] = useState('directive');
    const [regulator, setRegulator] = useState('');
    const [autoAnalyze, setAutoAnalyze] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState({ total: 0, done: 0, results: [] });
    const fileInputRef = useRef(null);
    
    const handleFileSelect = (e) => {
      const selectedFiles = Array.from(e.target.files || []);
      if (selectedFiles.length > 20) {
        toast.error('Maximum 20 files per batch');
        return;
      }
      setFiles(selectedFiles);
    };
    
    const handleUpload = async () => {
      if (files.length === 0) {
        toast.error('Please select files to upload');
        return;
      }
      
      setUploading(true);
      setProgress({ total: files.length, done: 0, results: [] });
      
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      formData.append('theme', theme);
      formData.append('document_type', docType);
      formData.append('regulator', regulator);
      formData.append('auto_analyze', autoAnalyze.toString());
      
      try {
        const res = await axios.post(`${API}/admin/aura-library/bulk-upload`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        setProgress({
          total: res.data.total,
          done: res.data.success_count,
          results: res.data.results
        });
        
        toast.success(`Uploaded ${res.data.success_count} of ${res.data.total} documents`);
        
        if (res.data.success_count > 0) {
          fetchDocuments();
          fetchStats();
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Bulk upload failed');
      } finally {
        setUploading(false);
      }
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white border border-border rounded-xl w-full max-w-xl overflow-hidden shadow-xl"
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Stack size={20} className="text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-primary">Bulk Upload</h3>
                <p className="text-xs text-text-tertiary">Upload multiple documents at once</p>
              </div>
            </div>
            <button onClick={() => setShowBulkUploadModal(false)} className="p-2 hover:bg-background-hover rounded-lg">
              <X size={20} className="text-text-tertiary" />
            </button>
          </div>
          
          <div className="p-6 space-y-4">
            {/* File Selection */}
            <div>
              <label className="block text-xs text-text-tertiary mb-1.5">Select Files (Max 20)</label>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-purple-500 transition-colors"
              >
                <FilePlus size={32} className="mx-auto text-text-tertiary mb-2" />
                <p className="text-sm text-text-primary">Click to select files</p>
                <p className="text-xs text-text-tertiary mt-1">PDF, DOC, DOCX accepted</p>
              </button>
              {files.length > 0 && (
                <div className="mt-3 max-h-32 overflow-y-auto border border-border rounded-lg p-2">
                  {files.map((file, i) => (
                    <div key={i} className="flex items-center justify-between py-1 text-sm">
                      <span className="text-text-primary truncate flex-1">{file.name}</span>
                      <span className="text-xs text-text-tertiary ml-2">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Theme & Type Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-text-tertiary mb-1.5">Theme *</label>
                <select value={theme} onChange={(e) => setTheme(e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm">
                  {REGULATORY_THEMES.map(t => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-text-tertiary mb-1.5">Document Type *</label>
                <select value={docType} onChange={(e) => setDocType(e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm">
                  {DOCUMENT_TYPES.map(t => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Regulator */}
            <div>
              <label className="block text-xs text-text-tertiary mb-1.5">Regulator</label>
              <input type="text" value={regulator} onChange={(e) => setRegulator(e.target.value)}
                placeholder="e.g., FIC, FSCA, SARB"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm" />
            </div>
            
            {/* Auto Analyze */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={autoAnalyze} onChange={(e) => setAutoAnalyze(e.target.checked)}
                className="w-4 h-4 rounded border-border text-purple-500" />
              <span className="text-sm text-text-primary">Auto-analyze with NAVI (extract effective dates & descriptions)</span>
            </label>
            
            {/* Progress */}
            {progress.results.length > 0 && (
              <div className="bg-background rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-text-primary font-medium">Upload Results</span>
                  <span className="text-xs text-text-tertiary">{progress.done}/{progress.total} successful</span>
                </div>
                <div className="max-h-40 overflow-y-auto space-y-2">
                  {progress.results.map((r, i) => (
                    <div key={i} className={`text-xs p-2 rounded ${r.status === 'success' ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                      <div className="flex items-center gap-2">
                        {r.status === 'success' ? <CheckCircle size={12} className="text-emerald-600" /> : <XCircle size={12} className="text-red-500" />}
                        <span className={`font-medium ${r.status === 'success' ? 'text-emerald-600' : 'text-red-500'}`}>
                          {r.document_name || r.filename}
                        </span>
                        {r.navi_title && (
                          <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-600 rounded text-[10px]">NAVI</span>
                        )}
                      </div>
                      {r.status === 'success' && (
                        <div className="mt-1 pl-5 text-text-tertiary">
                          <span className="font-mono">{r.reference_number}</span>
                          {r.effective_date && <span className="ml-2">• Effective: {r.effective_date}</span>}
                        </div>
                      )}
                      {r.status === 'error' && (
                        <div className="mt-1 pl-5 text-red-400">{r.error}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setShowBulkUploadModal(false)}
                className="px-4 py-2 border border-border rounded-lg text-sm text-text-secondary">Cancel</button>
              <button onClick={handleUpload} disabled={uploading || files.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
                {uploading ? (
                  <>
                    <ArrowsClockwise size={16} className="animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <CloudArrowUp size={16} />
                    Upload {files.length} File{files.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  };

  // ============================================
  // VERSION HISTORY MODAL
  // ============================================

  const VersionHistoryModal = () => {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNewVersionForm, setShowNewVersionForm] = useState(false);
    const [changeSummary, setChangeSummary] = useState('');
    const [newVersionFile, setNewVersionFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    
    useEffect(() => {
      if (documentForHistory?.id) {
        fetchVersions();
      }
    }, [documentForHistory]);
    
    const fetchVersions = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API}/admin/aura-library/documents/version-history/${documentForHistory.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setVersions(res.data.versions || []);
      } catch (error) {
        toast.error('Failed to load version history');
        setVersions([]);
      } finally {
        setLoading(false);
      }
    };
    
    const handleUploadNewVersion = async () => {
      if (!newVersionFile) {
        toast.error('Please select a file');
        return;
      }
      
      setUploading(true);
      const formData = new FormData();
      formData.append('file', newVersionFile);
      formData.append('change_summary', changeSummary);
      
      try {
        const res = await axios.post(
          `${API}/admin/aura-library/documents/${documentForHistory.id}/new-version`,
          formData,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
        );
        
        toast.success(res.data.message);
        setShowNewVersionForm(false);
        setNewVersionFile(null);
        setChangeSummary('');
        fetchVersions();
        fetchDocuments();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to upload new version');
      } finally {
        setUploading(false);
      }
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white border border-border rounded-xl w-full max-w-lg overflow-hidden shadow-xl"
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <ClockCounterClockwise size={20} className="text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-primary">Version History</h3>
                <p className="text-xs text-text-tertiary truncate max-w-xs">
                  {documentForHistory?.name || documentForHistory?.document_name}
                </p>
              </div>
            </div>
            <button onClick={() => { setShowVersionHistoryModal(false); setDocumentForHistory(null); }} 
              className="p-2 hover:bg-background-hover rounded-lg">
              <X size={20} className="text-text-tertiary" />
            </button>
          </div>
          
          <div className="p-6">
            {/* New Version Form */}
            {showNewVersionForm ? (
              <div className="space-y-4 mb-6 bg-purple-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-text-primary">Upload New Version</h4>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setNewVersionFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border border-dashed border-purple-300 rounded-lg p-4 text-center hover:border-purple-500 transition-colors"
                >
                  {newVersionFile ? (
                    <span className="text-sm text-purple-600">{newVersionFile.name}</span>
                  ) : (
                    <span className="text-sm text-text-tertiary">Click to select new PDF</span>
                  )}
                </button>
                <textarea
                  value={changeSummary}
                  onChange={(e) => setChangeSummary(e.target.value)}
                  placeholder="Describe what changed in this version..."
                  rows={2}
                  className="w-full px-3 py-2 bg-white border border-border rounded-lg text-sm resize-none"
                />
                <div className="flex gap-2">
                  <button onClick={() => setShowNewVersionForm(false)}
                    className="flex-1 px-3 py-2 border border-border rounded-lg text-sm text-text-secondary">
                    Cancel
                  </button>
                  <button onClick={handleUploadNewVersion} disabled={uploading || !newVersionFile}
                    className="flex-1 px-3 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
                    {uploading ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowNewVersionForm(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-dashed border-purple-300 text-purple-600 rounded-lg text-sm font-medium hover:bg-purple-50 mb-4"
              >
                <FilePlus size={16} />
                Upload New Version
              </button>
            )}
            
            {/* Version List */}
            <div className="space-y-3">
              <h4 className="text-xs text-text-tertiary font-medium uppercase">Version History</h4>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <ArrowsClockwise size={24} className="animate-spin text-text-tertiary" />
                </div>
              ) : versions.length === 0 ? (
                <p className="text-sm text-text-tertiary text-center py-4">No version history available</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {versions.map((v, i) => (
                    <div key={i} className={`p-3 rounded-lg border ${v.is_current ? 'border-purple-500 bg-purple-50' : 'border-border'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-mono font-medium text-text-primary">v{v.version}</span>
                          {v.is_current && (
                            <span className="px-2 py-0.5 bg-purple-500 text-white text-xs rounded-full">Current</span>
                          )}
                        </div>
                        <span className="text-xs text-text-tertiary">
                          {v.created_at ? new Date(v.created_at).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                      {v.change_summary && (
                        <p className="text-xs text-text-secondary mt-1">{v.change_summary}</p>
                      )}
                      {v.created_by && (
                        <p className="text-xs text-text-tertiary mt-1">By: {v.created_by}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="px-6 py-4 border-t border-border">
            <button onClick={() => { setShowVersionHistoryModal(false); setDocumentForHistory(null); }}
              className="w-full px-4 py-2 border border-border rounded-lg text-sm text-text-secondary hover:bg-background">
              Close
            </button>
          </div>
        </motion.div>
      </div>
    );
  };

  // ============================================
  // PENDING UPDATES MODAL (Tenant Mode)
  // ============================================
  
  const PendingUpdatesModal = () => {
    if (!showPendingUpdatesModal) return null;
    
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50" onClick={() => setShowPendingUpdatesModal(false)} />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-2xl bg-surface rounded-xl shadow-xl overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <Bell size={20} className="text-amber-600" weight="fill" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-primary">AURA Library Updates</h3>
                <p className="text-sm text-text-secondary">{pendingUpdates.length} document{pendingUpdates.length !== 1 ? 's' : ''} available</p>
              </div>
            </div>
            <button onClick={() => setShowPendingUpdatesModal(false)} className="p-2 hover:bg-background rounded-lg">
              <X size={20} className="text-text-tertiary" />
            </button>
          </div>
          
          {/* Content */}
          <div className="max-h-96 overflow-y-auto">
            {pendingUpdates.length === 0 ? (
              <div className="p-8 text-center">
                <CheckCircle size={48} className="mx-auto text-green-500 mb-3" />
                <p className="text-text-secondary">No pending updates</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {pendingUpdates.map((update) => (
                  <div key={update.id} className="p-4 hover:bg-background/50">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Files size={20} className="text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-text-primary">
                          {update.document_name || update.source_document?.name || 'New Document'}
                        </h4>
                        <p className="text-xs text-text-secondary mt-1">
                          {update.update_type === 'new_version' 
                            ? 'New version available' 
                            : 'New document from AURA Master Library'}
                        </p>
                        {update.source_document?.theme && (
                          <span className="inline-block mt-2 px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">
                            {update.source_document.theme.replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleAcceptPendingUpdate(update.id)}
                          disabled={acceptingUpdate === update.id}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                        >
                          {acceptingUpdate === update.id ? (
                            <ArrowsClockwise size={14} className="animate-spin" />
                          ) : (
                            <Check size={14} />
                          )}
                          Accept
                        </button>
                        <button
                          onClick={() => handleDeclinePendingUpdate(update.id)}
                          disabled={acceptingUpdate === update.id}
                          className="px-3 py-1.5 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="px-6 py-4 border-t border-border flex justify-end">
            <button
              onClick={() => setShowPendingUpdatesModal(false)}
              className="px-4 py-2 text-sm text-text-secondary border border-border rounded-lg hover:bg-background"
            >
              Close
            </button>
          </div>
        </motion.div>
      </div>
    );
  };

  // ============================================
  // MAIN RENDER
  // ============================================

  return (
    <div className={`flex h-screen bg-background ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
      {/* Sidebar - hidden in fullscreen mode */}
      {!isFullscreen && <Sidebar />}
      
      <div className={`flex-1 flex flex-col overflow-hidden ${isFullscreen ? 'ml-0' : 'ml-64'}`}>
        {/* Fullscreen Header Bar */}
        {isFullscreen && (
          <div className="px-6 py-3 bg-purple-600 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Files size={24} className="text-white" weight="fill" />
              <span className="text-lg font-semibold text-white">
                {isMasterMode ? 'AURA Master Library' : 'Regulatory Library'} - Expanded View
              </span>
            </div>
            <button
              onClick={() => setIsFullscreen(false)}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <ArrowsInSimple size={16} />
              Exit Fullscreen
            </button>
          </div>
        )}
        
        {/* Breadcrumb - hidden in fullscreen */}
        {!isFullscreen && (
          <div className="px-6 pt-4 bg-background">
            <PageBreadcrumb items={breadcrumbItems} />
          </div>
        )}
        
        {/* Pending Updates Banner (Tenant mode only) */}
        {!isMasterMode && pendingUpdatesCount > 0 && (
          <div className="mx-6 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <Bell size={20} className="text-amber-600" weight="fill" />
              </div>
              <div>
                <p className="text-sm font-medium text-amber-900">
                  {pendingUpdatesCount} new document{pendingUpdatesCount > 1 ? 's' : ''} available from AURA
                </p>
                <p className="text-xs text-amber-700">
                  Review and add to your regulatory library
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowPendingUpdatesModal(true)}
              className="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors"
            >
              Review Updates
            </button>
          </div>
        )}
        
        {/* Main Three-Panel Layout */}
        <div className="flex-1 flex overflow-hidden p-4 gap-4">
          {renderExplorePanel()}
          {renderDocumentTable()}
          {renderDetailPanel()}
        </div>
      </div>
      
      {/* Modals */}
      <AnimatePresence>
        {showUploadModal && <UploadModal />}
        {showPublishModal && isMasterMode && <PublishModal />}
        {showDeleteModal && <DeleteModal />}
        {showEditModal && <EditModal />}
        {showAddThemeModal && <AddThemeModal />}
        {showEditThemeModal && <EditThemeModal />}
        {showVersionHistoryModal && <VersionHistoryModal />}
        {showPendingUpdatesModal && !isMasterMode && <PendingUpdatesModal />}
        {showPublishToUniverseModal && !isMasterMode && <PublishToUniverseModal />}
        {showImpactModal && (
          <ImpactAnalysisModal
            isOpen={showImpactModal}
            onClose={() => {
              setShowImpactModal(false);
              setImpactResults(null);
              setImpactDirective(null);
            }}
            directive={impactDirective}
            results={impactResults}
            analyzing={analyzingImpact}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// PENDING UPDATES MODAL (Tenant Mode)
// ============================================

const PendingUpdatesModalComponent = ({ 
  isOpen, 
  onClose, 
  pendingUpdates, 
  onAccept, 
  onDecline, 
  acceptingUpdate 
}) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-2xl bg-surface rounded-xl shadow-xl overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <Bell size={20} className="text-amber-600" weight="fill" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text-primary">AURA Library Updates</h3>
              <p className="text-sm text-text-secondary">{pendingUpdates.length} document{pendingUpdates.length !== 1 ? 's' : ''} available</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-background rounded-lg">
            <X size={20} className="text-text-tertiary" />
          </button>
        </div>
        
        {/* Content */}
        <div className="max-h-96 overflow-y-auto">
          {pendingUpdates.length === 0 ? (
            <div className="p-8 text-center">
              <CheckCircle size={48} className="mx-auto text-green-500 mb-3" />
              <p className="text-text-secondary">No pending updates</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {pendingUpdates.map((update) => (
                <div key={update.id} className="p-4 hover:bg-background/50">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Files size={20} className="text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-text-primary">
                        {update.document_name || update.source_document?.name || 'New Document'}
                      </h4>
                      <p className="text-xs text-text-secondary mt-1">
                        {update.update_type === 'new_version' 
                          ? 'New version available' 
                          : 'New document from AURA Master Library'}
                      </p>
                      {update.source_document?.theme && (
                        <span className="inline-block mt-2 px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">
                          {update.source_document.theme.replace(/_/g, ' ')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onAccept(update.id)}
                        disabled={acceptingUpdate === update.id}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        {acceptingUpdate === update.id ? (
                          <ArrowsClockwise size={14} className="animate-spin" />
                        ) : (
                          <Check size={14} />
                        )}
                        Accept
                      </button>
                      <button
                        onClick={() => onDecline(update.id)}
                        disabled={acceptingUpdate === update.id}
                        className="px-3 py-1.5 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-text-secondary border border-border rounded-lg hover:bg-background"
          >
            Close
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// ============================================
// REGULATORY IMPACT ANALYSIS MODAL
// ============================================
const ImpactAnalysisModal = ({ 
  isOpen, 
  onClose, 
  directive, 
  results, 
  analyzing 
}) => {
  if (!isOpen) return null;
  
  const getRiskColor = (level) => {
    switch(level?.toLowerCase()) {
      case 'high': return 'text-red-500 bg-red-500/10 border-red-500/20';
      case 'medium': return 'text-amber-500 bg-amber-500/10 border-amber-500/20';
      case 'low': return 'text-green-500 bg-green-500/10 border-green-500/20';
      default: return 'text-gray-500 bg-gray-500/10 border-gray-500/20';
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-3xl bg-white rounded-xl shadow-xl overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
              <Brain size={20} className="text-teal-600" weight="fill" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Policy Impact Analysis</h3>
              <p className="text-sm text-gray-600 truncate max-w-[400px]">
                {directive?.name || directive?.title || 'Regulatory Directive'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} className="text-gray-500" />
          </button>
        </div>
        
        {/* Content */}
        <div className="max-h-[60vh] overflow-y-auto p-6 bg-white">
          {analyzing ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <Brain size={32} className="text-teal-600 animate-pulse" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">NAVI is Analyzing</h4>
              <p className="text-sm text-gray-600 text-center">
                Comparing this directive against your policies to identify potential impacts...
              </p>
            </div>
          ) : results ? (
            <div className="space-y-6">
              {/* Summary */}
              {results.impact_summary && (
                <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-teal-700 mb-2">Impact Summary</h4>
                  <p className="text-sm text-gray-700">{results.impact_summary}</p>
                </div>
              )}
              
              {/* Risk Level & Timeline */}
              <div className="flex items-center gap-4">
                {results.overall_risk_level && (
                  <div className={`px-3 py-1.5 rounded-full border text-sm font-medium ${getRiskColor(results.overall_risk_level)}`}>
                    {results.overall_risk_level.toUpperCase()} Risk
                  </div>
                )}
                {results.recommended_timeline && (
                  <div className="flex items-center gap-1 text-sm text-gray-600">
                    <CalendarBlank size={14} />
                    Recommended: {results.recommended_timeline}
                  </div>
                )}
                {results.alerts_created > 0 && (
                  <div className="flex items-center gap-1 text-sm text-amber-600">
                    <Bell size={14} />
                    {results.alerts_created} alert{results.alerts_created !== 1 ? 's' : ''} created
                  </div>
                )}
              </div>
              
              {/* Affected Policies */}
              {results.affected_policies?.length > 0 ? (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    Affected Policies ({results.affected_policies.filter(p => p.is_affected === 'yes').length})
                  </h4>
                  <div className="space-y-3">
                    {results.affected_policies.map((policy, idx) => (
                      <div 
                        key={idx}
                        className={`p-4 rounded-lg border ${
                          policy.is_affected === 'yes' 
                            ? 'bg-amber-50 border-amber-200' 
                            : policy.is_affected === 'uncertain'
                              ? 'bg-blue-50 border-blue-200'
                              : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h5 className="font-medium text-gray-900">{policy.policy_title}</h5>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              policy.is_affected === 'yes' 
                                ? 'bg-amber-100 text-amber-700' 
                                : policy.is_affected === 'uncertain'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-green-100 text-green-700'
                            }`}>
                              {policy.is_affected === 'yes' ? 'Affected' : 
                               policy.is_affected === 'uncertain' ? 'Uncertain' : 'Not Affected'}
                            </span>
                          </div>
                          {policy.urgency && (
                            <span className={`text-xs px-2 py-0.5 rounded-full border ${getRiskColor(policy.urgency)}`}>
                              {policy.urgency}
                            </span>
                          )}
                        </div>
                        
                        {policy.impact_areas?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {policy.impact_areas.map((area, aIdx) => (
                              <span key={aIdx} className="text-xs px-2 py-0.5 bg-gray-100 rounded text-gray-700">
                                {area}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        {policy.recommendation && (
                          <p className="text-sm text-gray-700">{policy.recommendation}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle size={48} className="mx-auto text-green-500 mb-3" />
                  <h4 className="font-medium text-gray-900 mb-1">No Policy Updates Required</h4>
                  <p className="text-sm text-gray-600">
                    This directive doesn't appear to impact your existing policies.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Warning size={48} className="mx-auto text-amber-500 mb-3" />
              <p className="text-gray-600">Unable to perform impact analysis</p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            Done
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default RegulatoryLibraryUnified;

// Named export for backward compatibility
export { RegulatoryLibraryUnified };
